<template>
  <div>
    <!-- Student Availability / Days Settings -->
    <b-sidebar
      id="sidebar-days-settings"
      bg-variant="white"
      v-model="visibilityDays"
      sidebar-class="sidebar-days-settings"
      right
      no-header
      backdrop
      shadow
      @shown="loadStudentAvailability()"
    >
      <div class="days-settings">
        <div class="days-settings__header">
          <div>
            <h5 class="mb-0">Days Settings</h5>
            <small class="text-muted">Configure off days by class & section</small>
          </div>
          <feather-icon
            class="cursor-pointer text-muted"
            icon="XIcon"
            size="18"
            @click="visibilityDays = false"
          />
        </div>

        <div class="days-settings__body">
          <div class="days-settings__card">
            <div class="days-settings__card-title">
              {{ availObj.id > 0 ? "Edit Rule" : "Add Rule" }}
            </div>

            <b-form @submit.prevent>
              <b-form-group label="Class" class="mb-1">
                <v-select
                  multiple
                  :closeOnSelect="false"
                  v-model="availObj.classIDs"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="classesOptions"
                  :clearable="false"
                  label="name"
                  :reduce="(val) => val.id"
                  placeholder="Select class"
                  @input="setAvailClass()"
                />
              </b-form-group>

              <b-form-group label="Section" class="mb-1">
                <v-select
                  multiple
                  :closeOnSelect="false"
                  v-model="availObj.sectionIDs"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="availSecOptions"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  label="section"
                  placeholder="Select section"
                  @input="setAvailSections()"
                />
              </b-form-group>

              <b-form-group label="Days" class="mb-1">
                <v-select
                  multiple
                  :closeOnSelect="false"
                  v-model="availObj.days"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="weekdayOptions"
                  :clearable="false"
                  placeholder="Select days"
                />
              </b-form-group>

              <div
                v-if="
                  (availObj.id > 0 && rights.edit) ||
                  (availObj.id === 0 && (rights.add || rights.edit))
                "
                class="d-flex"
                style="gap: 8px"
              >
                <b-button
                  variant="primary"
                  class="flex-grow-1"
                  :disabled="availSaving"
                  @click="saveStudentAvailability()"
                >
                  <b-spinner v-if="availSaving" small type="grow" />
                  <span v-else>{{
                    availObj.id > 0 ? "Update Rule" : "Save Rule"
                  }}</span>
                </b-button>
                <b-button
                  v-if="availObj.id > 0"
                  variant="outline-secondary"
                  :disabled="availSaving"
                  @click="resetAvailForm()"
                >
                  Cancel
                </b-button>
              </div>
            </b-form>
          </div>

          <div class="days-settings__list-head">
            <div>
              <div class="days-settings__card-title mb-0">Saved Rules</div>
              <small class="text-muted"
                >{{ availTotal || availList.length }} rule(s)</small
              >
            </div>
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="availLoading"
              @click="loadStudentAvailability()"
            >
              <feather-icon
                v-if="!availLoading"
                icon="RefreshCwIcon"
                size="14"
                class="mr-25"
              />
              <b-spinner v-else small type="grow" />
              Refresh
            </b-button>
          </div>

          <div v-if="availLoading" class="days-settings-skeleton">
            <div
              v-for="n in 4"
              :key="'avail-skel-' + n"
              class="days-settings-skeleton__card"
            >
              <div class="days-settings-skeleton__top">
                <div class="days-settings-skeleton__meta-grid">
                  <div
                    class="days-settings-skeleton__bar days-settings-skeleton__bar--meta"
                  ></div>
                  <div
                    class="days-settings-skeleton__bar days-settings-skeleton__bar--meta"
                  ></div>
                </div>
                <div class="days-settings-skeleton__actions">
                  <div
                    class="days-settings-skeleton__bar days-settings-skeleton__bar--icon"
                  ></div>
                  <div
                    class="days-settings-skeleton__bar days-settings-skeleton__bar--icon"
                  ></div>
                </div>
              </div>
              <div class="days-settings-skeleton__chips">
                <div
                  v-for="c in 3"
                  :key="'chip-' + n + '-' + c"
                  class="days-settings-skeleton__bar days-settings-skeleton__bar--chip"
                ></div>
              </div>
            </div>
          </div>

          <div
            v-else-if="!availList.length"
            class="days-settings__empty"
          >
            <feather-icon icon="InboxIcon" size="28" class="mb-50 text-muted" />
            <div class="font-weight-bold">No rules yet</div>
            <small class="text-muted"
              >Save a class/section + days rule above</small
            >
          </div>

          <template v-else>
            <div
              v-for="item in availList"
              :key="'avail-' + item.id"
              class="days-settings__rule"
            >
              <div class="days-settings__rule-top">
                <div class="days-settings__meta-grid">
                  <div class="days-settings__meta-card">
                    <span class="days-settings__meta-label">Class</span>
                    <span class="days-settings__meta-value">{{
                      formatAvailClasses(item)
                    }}</span>
                  </div>
                  <div class="days-settings__meta-card">
                    <span class="days-settings__meta-label">Section</span>
                    <span class="days-settings__meta-value">{{
                      formatAvailSections(item)
                    }}</span>
                  </div>
                </div>

                <div
                  v-if="rights.edit || rights.delete"
                  class="days-settings__actions days-settings__actions--icons"
                >
                  <b-button
                    v-if="rights.edit"
                    size="sm"
                    variant="outline-primary"
                    class="btn-icon days-settings__icon-btn"
                    v-b-tooltip.hover.top
                    title="Edit"
                    @click="editStudentAvailability(item)"
                  >
                    <feather-icon icon="EditIcon" size="14" />
                  </b-button>
                  <b-button
                    v-if="rights.delete"
                    size="sm"
                    variant="outline-danger"
                    class="btn-icon days-settings__icon-btn"
                    v-b-tooltip.hover.top
                    title="Delete"
                    :disabled="availDeleting === item.id"
                    @click="deleteStudentAvailability(item)"
                  >
                    <b-spinner
                      v-if="availDeleting === item.id"
                      small
                      type="grow"
                    />
                    <feather-icon v-else icon="Trash2Icon" size="14" />
                  </b-button>
                </div>
              </div>

              <div class="days-settings__days-wrap">
                <span
                  v-for="day in splitAvailDays(item.days)"
                  :key="'day-' + item.id + '-' + day"
                  class="days-settings__day"
                >
                  {{ day.slice(0, 3) }}
                </span>
                <span
                  v-if="!splitAvailDays(item.days).length"
                  class="days-settings__day days-settings__day--empty"
                >
                  No days
                </span>
              </div>
            </div>
          </template>

          <div
            v-if="!availLoading && availTotal > availPageSize"
            class="days-settings__pager"
          >
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="availPage <= 1 || availLoading"
              @click="changeAvailPage(availPage - 1)"
            >
              Prev
            </b-button>
            <small>Page {{ availPage }}</small>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="
                availPage * availPageSize >= availTotal || availLoading
              "
              @click="changeAvailPage(availPage + 1)"
            >
              Next
            </b-button>
          </div>
        </div>
      </div>
    </b-sidebar>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Settings") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hideSideBar()"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="pt-1 pb-1">
          <b-col
            class="d-flex align-items-center justify-content-between mb-1"
            md="12"
          >
            <h4 class="mr-1">{{ $t("Barcode") }}</h4>
            <b-form-checkbox
              v-model="settings.attendanceMode"
              switch
              value="barcode"
              unchecked-value=""
            />
          </b-col>
          <b-col
            class="d-flex align-items-center justify-content-between mb-1"
            md="12"
          >
            <h4 class="mr-1">{{ $t("Notification on arrival") }}</h4>
            <b-form-checkbox v-model="settings.notifyArrival" switch />
          </b-col>
          <!-- <b-col
              class="d-flex align-items-center justify-content-between mb-1"
              md="12"
            >
              <h4 class="mr-1">Notification on departure</h4>
              <b-form-checkbox v-model="settings.notifyDeparture" switch />
            </b-col> -->
          <b-col
            class="d-flex align-items-center justify-content-between mb-1"
            md="12"
          >
            <h4 class="mr-1">{{ $t("Show dues") }}</h4>
            <b-form-checkbox v-model="settings.showDues" switch />
          </b-col>
          <b-col
            class="d-flex align-items-center justify-content-between mb-1"
            md="12"
          >
            <h4 class="mr-1">{{ $t("Show recent attendance") }}</h4>
            <b-form-checkbox v-model="settings.showRecent" switch />
          </b-col>
          <b-col
            class="d-flex align-items-center justify-content-between mb-1"
            md="12"
          >
            <h4 class="mr-1">{{ $t("Auto hide") }}</h4>
            <b-form-checkbox v-model="settings.autoHide" switch />
          </b-col>
          <b-col v-if="settings.autoHide" md="12">
            <h4 class="mr-1">
              {{ $t("Hide After") }}
              <span class="font-small-4">({{ $t("seconds") }})</span>
            </h4>
            <b-form-input
              id="mc-first-name"
              type="number"
              placeholder=""
              ref="autosec"
              v-model="settings.hideAfterSeconds"
            />
          </b-col>
          <b-col class="mt-1" md="12">
            <b-button
              variant="success"
              @click="saveSettings()"
              :disabled="request"
              block
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else>{{ $t("Save") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility6"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Mark Status") }}</h5>
        <div class="d-flex align-items-center">
          <b-button
            variant="outline-primary"
            class="btn-icon mr-50"
            size="sm"
            v-b-tooltip.hover.top
            :title="$t('Status History')"
            @click="openStudentHolidayHistory()"
          >
            <feather-icon icon="ClockIcon" size="16" />
          </b-button>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="visibility6 = false"
          />
        </div>
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="mt-1">
          <b-col md="12">
            <b-form-group :label="$t('Status')" ref="hol_status">
              <v-select
                v-model="holiObj.statusType"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="markStatusOptions"
                :reduce="(val) => val.value"
                label="label"
                :clearable="false"
                :placeholder="$t('Select status')"
              />
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="false">
            <b-form-group
              :label="$t('Department')"
              :invalid-feedback="$t('Department is required')"
              ref="hol_dep"
            >
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="holiObj.departmentIDs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="departments"
                :reduce="(val) => val.id"
                label="name"
                :clearable="false"
                @input="setHoliDept()"
                ref="hol_dep"
                :placeholder="$t('Select department')"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Select ' + getLabel('Class'))"
              :invalid-feedback="$t(getLabel('class') + ' is required')"
              ref="hol_class"
            >
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="holiObj.clsIDs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="classesOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                :placeholder="$t('Select ' + getLabel('class'))"
                @input="setHoliClass()"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Select ' + getLabel('Section'))"
              :invalid-feedback="$t(getLabel('section') + ' is required')"
              ref="hol_section"
            >
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="holiObj.secIDs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="holiSecOptions"
                :reduce="(val) => val.id"
                :clearable="false"
                label="section"
                :placeholder="$t('Select ' + getLabel('section'))"
                @input="setHoliSections()"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Date From')"
              :invalid-feedback="$t('date is required')"
              ref="hol_dateF"
            >
              <flat-pickr
                ref="hol_dateF"
                :config="config"
                v-model="holiObj.dtFrom"
                class="form-control"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Date Till')"
              :invalid-feedback="$t('date is required')"
              ref="hol_dateT"
            >
              <flat-pickr
                ref="hol_dateT"
                :config="config"
                v-model="holiObj.dtTo"
                class="form-control"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="holiObj.statusType !== 'off'">
            <b-form-group :label="$t('Reason')" ref="hol_reason">
              <b-form-input
                v-model.trim="holiObj.reason"
                :placeholder="$t('Enter reason (optional)')"
              />
            </b-form-group>
          </b-col>

          <b-col class="mt-1" md="12">
            <b-button
              variant="success"
              @click="markAttendanceStatus()"
              :disabled="marking"
              block
            >
              <b-spinner v-if="marking" small type="grow" />
              <span v-else>{{ $t("Save") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <!-- Student Holiday History Modal -->
    <b-modal
      v-model="studentHolidayHistoryVisible"
      hide-header
      hide-footer
      size="xl"
      centered
      no-close-on-backdrop
      body-class="p-0"
      content-class="holiday-history-modal"
    >
      <div class="hh-modal">
        <div class="hh-header">
          <div class="hh-header-left">
            <div class="hh-header-icon">
              <feather-icon icon="ClockIcon" size="18" />
            </div>
            <div>
              <div class="hh-header-title">{{ $t("Status History") }}</div>
              <div class="hh-header-sub">
                {{
                  $t(
                    "View and remove holidays, exam leave, study leave, and off marks"
                  )
                }}
              </div>
            </div>
          </div>
          <button
            type="button"
            class="hh-close-btn"
            @click="studentHolidayHistoryVisible = false"
          >
            <feather-icon icon="XIcon" size="16" />
          </button>
        </div>

        <div class="hh-filters">
          <div class="hh-filter-field">
            <label>{{ $t("Date From") }}</label>
            <flat-pickr
              v-model="studentHolidayHistoryFilters.dtFrom"
              :config="config"
              class="form-control hh-date-input"
              :placeholder="$t('Select Date')"
            />
          </div>
          <div class="hh-filter-field">
            <label>{{ $t("Date Till") }}</label>
            <flat-pickr
              v-model="studentHolidayHistoryFilters.dtTo"
              :config="config"
              class="form-control hh-date-input"
              :placeholder="$t('Select Date')"
            />
          </div>
          <div class="hh-filter-field">
            <label>{{ $t("Status") }}</label>
            <v-select
              v-model="studentHolidayHistoryFilters.status"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="studentHolidayHistoryStatusFilterOptions"
              :reduce="(val) => val.value"
              label="label"
              :clearable="false"
              :placeholder="$t('All types')"
            />
          </div>
          <div class="hh-filter-actions">
            <b-button
              variant="primary"
              class="hh-btn-search"
              :disabled="studentHolidayHistoryLoading"
              @click="loadStudentHolidayHistory(1)"
            >
              <b-spinner
                v-if="studentHolidayHistoryLoading"
                small
                type="grow"
              />
              <template v-else>
                <feather-icon icon="SearchIcon" size="14" class="mr-50" />
                {{ $t("Search") }}
              </template>
            </b-button>
            <b-button
              variant="flat-secondary"
              class="hh-btn-clear"
              :disabled="studentHolidayHistoryLoading"
              @click="resetStudentHolidayHistoryFilters()"
            >
              {{ $t("Clear") }}
            </b-button>
          </div>
        </div>

        <div class="hh-table-wrap">
          <b-table
            :items="studentHolidayHistoryItems"
            :fields="visibleStudentHolidayHistoryFields"
            :busy="studentHolidayHistoryLoading"
            show-empty
            responsive
            hover
            borderless
            class="holiday-history-table"
          >
            <template #table-busy>
              <div class="hh-busy">
                <b-spinner variant="primary" />
              </div>
            </template>
            <template #empty>
              <div class="hh-empty">
                <feather-icon icon="CalendarIcon" size="28" class="mb-50" />
                <div>{{ $t("No records found") }}</div>
              </div>
            </template>
            <template #cell(campusName)="data">
              <span class="hh-campus">{{
                formatHolidayHistoryCampus(data.item)
              }}</span>
            </template>
            <template #cell(dtFrom)="data">
              <span class="hh-date-chip">{{
                formatStudentHolidayDate(data.item.dtFrom)
              }}</span>
            </template>
            <template #cell(dtTo)="data">
              <span class="hh-date-chip">{{
                formatStudentHolidayDate(data.item.dtTo)
              }}</span>
            </template>
            <template #cell(status)="data">
              <b-badge
                pill
                :variant="
                  holidayHistoryStatusVariant(
                    resolveHolidayHistoryRowStatus(data.item)
                  )
                "
                class="hh-status-badge"
              >
                {{
                  holidayHistoryStatusLabel(
                    resolveHolidayHistoryRowStatus(data.item)
                  )
                }}
              </b-badge>
            </template>
            <template #cell(reason)="data">
              <span class="hh-reason">{{ data.item.reason || "—" }}</span>
            </template>
            <template #cell(clsIDs)="data">
              <span class="hh-dept-pill">{{
                formatStudentHolidayClasses(data.item.clsIDs)
              }}</span>
            </template>
            <template #cell(secIDs)="data">
              <span class="hh-dept-pill">{{
                formatStudentHolidaySections(data.item.secIDs)
              }}</span>
            </template>
            <template #cell(createdByName)="data">
              {{
                data.item.createdByName ||
                data.item.createdBy ||
                data.item.userName ||
                "—"
              }}
            </template>
            <template #cell(createdAt)="data">
              <span class="hh-muted">{{
                formatStudentHolidayDateTime(data.item.createdAt)
              }}</span>
            </template>
            <template #cell(actions)="data">
              <div class="hh-actions">
                <button
                  type="button"
                  class="hh-del-btn"
                  :disabled="
                    studentHolidayHistoryDeletingId ===
                    holidayHistoryRowId(data.item)
                  "
                  v-b-tooltip.hover.top
                  :title="$t('Delete')"
                  @click="deleteStudentHolidayHistory(data.item)"
                >
                  <b-spinner
                    v-if="
                      studentHolidayHistoryDeletingId ===
                      holidayHistoryRowId(data.item)
                    "
                    small
                    type="grow"
                  />
                  <feather-icon v-else icon="Trash2Icon" size="15" />
                </button>
              </div>
            </template>
          </b-table>
        </div>

        <div v-if="studentHolidayHistoryTotal > 0" class="hh-footer">
          <span class="hh-footer-count">
            {{ $t("Showing") }}
            <strong>{{ studentHolidayHistoryRange.start }}</strong>
            –
            <strong>{{ studentHolidayHistoryRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ studentHolidayHistoryTotal }}</strong>
          </span>
          <b-pagination
            v-if="
              studentHolidayHistoryTotal > studentHolidayHistoryPageSize
            "
            v-model="studentHolidayHistoryPage"
            :total-rows="studentHolidayHistoryTotal"
            :per-page="studentHolidayHistoryPageSize"
            size="sm"
            class="mb-0 hh-pagination"
            @input="loadStudentHolidayHistory"
          />
        </div>
      </div>
    </b-modal>

    <!-- Holiday Summary Modal -->
    <b-modal
      v-model="holidaySummaryVisible"
      hide-header
      hide-footer
      centered
      size="lg"
      body-class="p-0"
    >
      <div v-if="holidaySummary" class="hs-modal">
        <div class="hs-header">
          <div class="hs-header-left">
            <div
              class="hs-header-icon"
              :class="{ 'hs-header-icon--warn': holidaySummaryHasWarn }"
            >
              <feather-icon
                :icon="
                  holidaySummaryHasWarn
                    ? 'AlertTriangleIcon'
                    : 'CheckCircleIcon'
                "
                size="20"
              />
            </div>
            <div>
              <div class="hs-header-title">{{ $t("Status Summary") }}</div>
              <div class="hs-header-meta">
                <span class="hs-meta-badge">
                  <feather-icon icon="CalendarIcon" size="11" />
                  {{ holidaySummary.dtFrom }} → {{ holidaySummary.dtTo }}
                </span>
                <span
                  class="hs-meta-badge hs-meta-badge--reason"
                  v-if="holidaySummary.reason"
                >
                  <feather-icon icon="TagIcon" size="11" />
                  {{ holidaySummary.reason }}
                </span>
                <span
                  v-if="holidaySummary.totalStudents != null"
                  class="hs-meta-badge"
                >
                  <feather-icon icon="UsersIcon" size="11" />
                  {{ holidaySummary.totalStudents }} students
                </span>
                <span
                  v-if="holidaySummary.totalDates != null"
                  class="hs-meta-badge"
                >
                  {{ holidaySummary.totalDates }} day(s)
                </span>
              </div>
            </div>
          </div>
          <button class="hs-close-btn" @click="holidaySummaryVisible = false">
            <feather-icon icon="XIcon" size="16" />
          </button>
        </div>

        <div
          v-if="holidaySummary.message"
          class="hs-message"
          :class="{ 'hs-message--warn': holidaySummaryHasWarn }"
        >
          <feather-icon
            :icon="holidaySummaryHasWarn ? 'AlertCircleIcon' : 'InfoIcon'"
            size="14"
          />
          <span>{{ holidaySummary.message }}</span>
        </div>

        <div class="hs-stats">
          <div class="hs-stat hs-stat--green">
            <div class="hs-stat-num">{{ holidaySummary.created || 0 }}</div>
            <div class="hs-stat-lbl">Created</div>
          </div>
          <div class="hs-stat hs-stat--blue">
            <div class="hs-stat-num">{{ holidaySummary.updated || 0 }}</div>
            <div class="hs-stat-lbl">Updated</div>
          </div>
          <div class="hs-stat hs-stat--orange">
            <div class="hs-stat-num">
              {{ holidaySummary.alreadyHoliday || 0 }}
            </div>
            <div class="hs-stat-lbl">Already</div>
          </div>
          <div class="hs-stat hs-stat--purple">
            <div class="hs-stat-num">{{ holidaySummaryTotalProcessed }}</div>
            <div class="hs-stat-lbl">Total Processed</div>
          </div>
        </div>

        <div class="hs-footer">
          <b-button
            variant="primary"
            @click="holidaySummaryVisible = false"
            class="hs-done-btn"
          >
            Done
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility5"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Settings") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility5 = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="pt-1 pb-1">
          <b-col
            v-if="checkInObj"
            class="d-flex align-items-center justify-content-between mb-1"
            md="12"
          >
            <h4 class="mr-1">Show Checkin</h4>
            <b-form-checkbox v-model="checkInObj.valueBool" switch />
          </b-col>
          <!-- <b-col
           v-if='checkOutObj'
            class="d-flex align-items-center justify-content-between mb-1"
            md="12"
          >
            <h4 class="mr-1">Show Checkout</h4>
            <b-form-checkbox v-model="checkOutObj.valueBool" switch />
          </b-col> -->

          <b-col class="mt-1" md="12">
            <b-button
              variant="success"
              @click="saveChecks()"
              :disabled="savingCh"
              block
            >
              <b-spinner v-if="savingCh" small type="grow" />
              <span v-else>{{ $t("Save") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility4"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Smart Attendance") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility4 = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="pt-1 pb-1">
          <b-col md="12">
            <b-form-group
              :label="$t('Date')"
              :invalid-feedback="$t('Date is required')"
              ref="sm_date"
            >
              <flat-pickr
                :config="config2"
                v-model="smartObj.dt"
                class="form-control"
                :placeholder="$t('Select Date')"
                @on-change="setSmartDate"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Gr Numbers')"
              :invalid-feedback="$t('Please enter gr separated by space')"
              ref="grs"
            >
              <b-form-textarea
                ref="grs"
                :placeholder="$t('Enter gr numbers seperated by space')"
                v-model="smartObj.gr"
                rows="4"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
          <b-col md="12" class="mt-1">
            <b-form-group
              :label="$t('Status')"
              :invalid-feedback="$t('Status is required')"
              ref="sm_status"
            >
              <v-select
                ref="sm_status"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :clearable="false"
                v-model="smartObj.status"
                :options="smartOptions"
                :reduce="(opt) => opt.value"
                label="name"
                :placeholder="$t('select status')"
              />
            </b-form-group>
          </b-col>
          <b-col class="mt-1" md="12">
            <b-button
              variant="success"
              @click="saveSmart()"
              :disabled="request"
              block
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else>{{ $t("Save") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility3"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Reports") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility3 = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="pt-1 pb-1">
          <b-col md="12" v-if="reportDomain === 'myskoolksa'">
            <b-form-group :label="$t('Report Type')" class="mb-2">
              <v-select
                v-model="reportType"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="reportOptions"
                :reduce="(val) => val.value"
                label="text"
                :clearable="false"
                :placeholder="$t('Select type')"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Select ' + getLabel('Class'))"
              :invalid-feedback="$t(getLabel('class') + ' is required')"
              ref="rep_class"
            >
              <v-select
                ref="rep_class"
                multiple
                :closeOnSelect="false"
                v-model="repClass"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="classesOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                @input="FillRepSections()"
                :placeholder="$t('Select ' + getLabel('class'))"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Select ' + getLabel('Section'))"
              :invalid-feedback="$t(getLabel('section') + ' is required')"
              ref="rep_section"
            >
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="repSection"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="repSecOptions"
                :reduce="(val) => val.id"
                :clearable="false"
                @input="checkRepSections()"
                ref="rep_section"
                label="section"
                :placeholder="$t('Select ' + getLabel('section'))"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Date')"
              :invalid-feedback="$t('date is required')"
              ref="rep_date"
            >
              <flat-pickr
                ref="rep_date"
                :config="config2"
                v-model="repDate"
                class="form-control"
                :placeholder="$t('Select Date')"
                @on-change="setRepDate"
              />
            </b-form-group>
          </b-col>
          <b-col md="12" class="mt-1">
            <b-form-group
              :label="$t('Select Status')"
              :invalid-feedback="$t('status is required')"
              ref="rep_status"
            >
              <v-select
                multiple
                :closeOnSelect="false"
                ref="rep_status"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :clearable="false"
                v-model="repObj.status"
                :options="statusOptions"
                :reduce="(opt) => opt.value"
                label="name"
                :placeholder="$t('select status')"
                @input="setStatus()"
              />
            </b-form-group>
          </b-col>
          <b-col class="mt-1" md="12">
            <b-button
              variant="success"
              @click="getReport()"
              :disabled="request"
              block
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else>{{ $t("Show") }}</span>
            </b-button>
          </b-col>
          <b-col class="mt-1" md="12">
            <b-button
              variant="success"
              @click="getSheet()"
              :disabled="sheetLoading"
              block
            >
              <b-spinner v-if="sheetLoading" small type="grow" />
              <span v-else>{{ $t("Attendance Sheet") }}</span>
            </b-button>
          </b-col>
          <b-col class="mt-1" md="12">
            <b-button
              variant="warning"
              @click="exportReport()"
              :disabled="exporting"
              block
            >
              <b-spinner v-if="exporting" small type="grow" />
              <span v-else>{{ $t("Export") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility2"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Send Message") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility2 = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="pt-1 pb-1 align-items-center">
          <b-col md="12">
            <b-form-group
              :label="$t('Select Class')"
              :invalid-feedback="$t('class is required')"
              ref="msg_class"
            >
              <v-select
                ref="msg_class"
                multiple
                :closeOnSelect="false"
                v-model="msgClass"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="classesOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                @input="FillMsgSections()"
                :placeholder="$t('Select class')"
              />
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="msgClass[0] != 0">
            <b-form-group
              :label="$t('Select Section')"
              :invalid-feedback="$t('section is required')"
              ref="msg_section"
            >
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="msgSection"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="msgSecOptions"
                :reduce="(val) => val.id"
                :clearable="false"
                @input="checkMsgSections()"
                ref="msg_section"
                label="section"
                :placeholder="$t('Select section')"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Date')"
              :invalid-feedback="$t('date is required')"
              ref="msg_date"
            >
              <flat-pickr
                ref="msg_date"
                :config="config"
                v-model="msgObj.dt"
                class="form-control"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </b-col>
          <b-col md="12" class="mt-1">
            <b-form-group
              :label="$t('Select Status')"
              :invalid-feedback="$t('status is required')"
              ref="msg_status"
            >
              <v-select
                ref="msg_status"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :clearable="false"
                v-model="msgObj.status"
                :options="statusOptions"
                :reduce="(opt) => opt.value"
                label="name"
                :placeholder="$t('select status')"
              />
            </b-form-group>
          </b-col>
          <b-col class="mt-1" md="12">
            <b-button
              variant="success"
              @click="saveMessage()"
              :disabled="request"
              block
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else>{{ $t("Send Message") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-modal
      id="modal-att"
      cancel-variant="outline-secondary"
      hide-footer
      centered
      no-close-on-backdrop
      title="Edit Attendance"
    >
      <b-row class="">
        <b-col>
          <b-button
            pill
            size="sm"
            style="margin-inline: 2px"
            :variant="currentObj.status == 'P' ? 'success' : 'outline-success'"
            @click="currentObj.status = 'P'"
          >
            P
          </b-button>
          <b-button
            pill
            size="sm"
            style="margin-inline: 2px"
            :variant="currentObj.status == 'A' ? 'danger' : 'outline-danger'"
            @click="currentObj.status = 'A'"
          >
            A
          </b-button>
          <b-button
            pill
            size="sm"
            style="margin-inline: 2px"
            :variant="currentObj.status == 'L' ? 'info' : 'outline-info'"
            @click="currentObj.status = 'L'"
          >
            L
          </b-button>
          <b-button
            pill
            size="sm"
            style="margin-inline: 2px"
            :variant="
              currentObj.status.toLowerCase() == 'late'
                ? 'warning'
                : 'outline-warning'
            "
            @click="currentObj.status = 'Late'"
          >
            Late
          </b-button>
          <b-button
            pill
            size="sm"
            style="margin-inline: 2px"
            :variant="currentObj.status == 'H' ? 'info' : 'outline-info'"
            @click="currentObj.status = 'H'"
          >
            H
          </b-button>
          <b-button
            pill
            size="sm"
            style="margin-inline: 2px"
            :variant="
              currentObj.status == 'Off' ? 'secondary' : 'outline-secondary'
            "
            @click="currentObj.status = 'Off'"
          >
            Off
          </b-button>
          <b-button
            pill
            size="sm"
            style="margin-inline: 2px"
            v-b-tooltip.hover.top
            title="Exam Leave"
            :variant="
              currentObj.status == 'EXL' ? 'dark' : 'outline-dark'
            "
            @click="currentObj.status = 'EXL'"
          >
            EXL
          </b-button>
          <b-button
            pill
            size="sm"
            style="margin-inline: 2px"
            v-b-tooltip.hover.top
            title="Study Leave"
            :variant="
              currentObj.status == 'STL' ? 'primary' : 'outline-primary'
            "
            @click="currentObj.status = 'STL'"
          >
            STL
          </b-button>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col class="d-flex justify-content-end">
          <b-button
            @click="saveAtt()"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="success"
            :disabled="savingAtt"
          >
            <b-spinner v-if="savingAtt" small type="grow" />
            <span v-else class="align-middle">{{ $t("Save") }}</span>
          </b-button>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Details -->
    <b-row v-if="showreport">
      <b-col md="12" v-if="showDetails">
        <b-row class="mb-1">
          <b-col>
            <b-media vertical-align="center">
              <template #aside>
                <b-button
                  variant="outline-secondary"
                  class="btn-icon mr-1 rounded-circle"
                  @click="showDetails = false"
                  v-b-tooltip.hover.top
                  :title="$t('Back')"
                >
                  <feather-icon class="" size="18" icon="ArrowLeftIcon" />
                  <!-- Back -->
                </b-button>
                <b-avatar
                  size="44"
                  :src="detailObj.picture"
                  variant="light-primary"
                />
              </template>

              <b-link class="font-weight-bold d-block text-nowrap mb-25">
                <b-badge variant="light-primary">
                  {{ detailObj.name }}
                </b-badge>
              </b-link>
              <b-link class="font-weight-bold d-block text-nowrap mb-25">
                <b-badge variant="light-primary">
                  F/N: {{ detailObj.father_name }}
                </b-badge>
              </b-link>
              <small class="text-muted">
                <b-badge variant="light-primary">
                  {{ detailObj.cls }}
                </b-badge>
              </small>
            </b-media>
          </b-col>
        </b-row>
        <b-row align-h="center" v-if="detailLoading" class="mb-2">
          <b-spinner
            style="width: 3rem; height: 3rem"
            variant="primary"
            label="Spinning"
          ></b-spinner>
        </b-row>
        <b-row class="hover-card" v-else>
          <b-col md="3">
            <b-card bg-variant="success" text-variant="white" class="">
              <h4 class="text-white font-weight-bolder">Present</h4>
              <p class="font-weight-bolder mb-0 text-right summary-count">
                {{ countObj.present }}
              </p>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card bg-variant="danger" text-variant="white" class="">
              <h4 class="text-white font-weight-bolder">Absent</h4>
              <p class="font-weight-bolder mb-0 text-right summary-count">
                {{ countObj.absent }}
              </p>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card bg-variant="info" text-variant="white" class="">
              <h4 class="text-white font-weight-bolder">Leave</h4>
              <p class="font-weight-bolder mb-0 text-right summary-count">
                {{ countObj.leave }}
              </p>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card bg-variant="warning" text-variant="white" class="">
              <h4 class="text-white font-weight-bolder">Late</h4>
              <p class="font-weight-bolder mb-0 text-right summary-count">
                {{ countObj.late }}
              </p>
            </b-card>
          </b-col>
        </b-row>
        <b-card>
          <!-- <b-button
            variant="outline-primary"
            @click="loadDetails('week')"
            class="mr-1"
          >
            This Week
          </b-button>
          <b-button
            variant="outline-primary"
            @click="loadDetails('month')"
            class="mr-1"
          >
            This Month
          </b-button>
          <b-button
            variant="outline-primary"
            @click="loadDetails('year')"
            class="mr-1"
          >
            This Year
          </b-button>
          <b-button
            variant="outline-primary"
            @click="loadDetails('last year')"
            class="mr-1"
          >
            Last Year
          </b-button> -->

          <b-row>
            <b-col md="12" class="d-flex align-items-start flex-wrap">
              <b-button
                class="btn-icon mr-50 mb-1"
                v-for="mon in months"
                :key="mon.value"
                :variant="attMonth == mon.value ? 'primary' : 'outline-primary'"
                @click="
                  attMonth = mon.value;
                  moveCalendar();
                  loadDetails('');
                "
              >
                {{ mon.text.slice(0, 3) }}
              </b-button>
              <div style="width: 120px">
                <b-form-group label="">
                  <v-select
                    v-model="attYear"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="years"
                    :clearable="false"
                    placeholder="Select year"
                    @input="
                      moveCalendar();
                      loadDetails('');
                    "
                  />
                </b-form-group>
              </div>
            </b-col>
          </b-row>
          <full-calendar
            ref="refCalendar"
            :options="calendarOptions"
            class="full-calendar mt-1"
          >
            <template v-slot:eventContent="arg">
              <b class="font-large-3">{{ arg.event.title }}</b>
              <!-- <p v-if="arg.event.start" class="font-small-2 mb-25">
                <span class="text-dark text-uppercase">Time in: </span>
                <b-badge
                  :variant="getVariant(arg.event.title)"
                  class="font-small-2"
                >
                  {{
                    new Intl.DateTimeFormat("en-US", {
                      hour: "numeric",
                      minute: "numeric",
                    }).format(new Date(arg.event.start))
                  }}
                </b-badge>
              </p> -->
            </template>
          </full-calendar>

          <b-table
            v-if="false"
            responsive
            :items="detailItems"
            :fields="detailFields"
            class="mt-1"
            show-empty
            :busy="detailLoading"
          >
            <template #table-busy>
              <div class="d-flex justify-content-center mb-3 mt-3">
                <b-spinner
                  style="width: 3rem; height: 3rem"
                  variant="primary"
                  label="Spinning"
                  type="grow"
                ></b-spinner>
              </div>
            </template>
            <template #cell(status)="data">
              <b-badge
                :variant="
                  data.value == 'P'
                    ? 'light-success'
                    : data.value == 'A'
                    ? 'light-danger'
                    : data.value == 'L' || data.value == 'H'
                    ? 'light-info'
                    : 'light-warning'
                "
              >
                {{ data.item.status }}
              </b-badge>
            </template>
            <template #cell(date)="data">
              <b-badge variant="light-primary">
                {{
                  new Date(data.value).toLocaleDateString("en-UK", {
                    year: "numeric",
                    day: "numeric",
                    month: "short",
                  })
                }}
              </b-badge>
            </template>
            <template #cell(time)="data">
              <b-badge variant="light-primary">
                {{
                  new Intl.DateTimeFormat("en-US", {
                    hour: "numeric",
                    minute: "numeric",
                  }).format(new Date(data.item.date))
                }}
              </b-badge>
            </template>
          </b-table>
        </b-card>
      </b-col>

      <template v-else>
        <b-col md="12">
          <b-row align-h="center" v-if="summLoading" class="mb-2">
            <b-spinner
              style="width: 3rem; height: 3rem"
              variant="primary"
              label="Spinning"
            ></b-spinner>
          </b-row>
          <b-row class="" v-else>
            <b-col xl="2" md="4">
              <b-card body-class="pl-1">
                <b-media no-body>
                  <b-media-aside class="mr-50">
                    <b-avatar size="44" variant="light-success">
                      <feather-icon size="24" icon="UserCheckIcon" />
                    </b-avatar>
                  </b-media-aside>
                  <b-media-body class="">
                    <h4 class="font-weight-bolder mb-0">{{ $t("Present") }}</h4>
                    <b-card-text class="mb-0">
                      {{ summaryObj.present }}
                    </b-card-text>
                  </b-media-body>
                </b-media>
              </b-card>
            </b-col>
            <b-col xl="2" md="4">
              <b-card body-class="pl-1">
                <b-media no-body>
                  <b-media-aside class="mr-50">
                    <b-avatar size="44" variant="light-danger">
                      <feather-icon size="24" icon="UserXIcon" />
                    </b-avatar>
                  </b-media-aside>
                  <b-media-body class="">
                    <h4 class="font-weight-bolder mb-0">{{ $t("Absent") }}</h4>
                    <b-card-text class="mb-0">
                      {{ summaryObj.absent }}
                    </b-card-text>
                  </b-media-body>
                </b-media>
              </b-card>
            </b-col>
            <b-col xl="2" md="4">
              <b-card body-class="pl-1">
                <b-media no-body>
                  <b-media-aside class="mr-50">
                    <b-avatar size="44" variant="light-info">
                      <feather-icon size="24" icon="PlusCircleIcon" />
                    </b-avatar>
                  </b-media-aside>
                  <b-media-body class="">
                    <h4 class="font-weight-bolder mb-0">{{ $t("Leave") }}</h4>
                    <b-card-text class="mb-0">
                      {{ summaryObj.leave }}
                    </b-card-text>
                  </b-media-body>
                </b-media>
              </b-card>
            </b-col>
            <b-col xl="2" md="4">
              <b-card body-class="pl-1">
                <b-media no-body>
                  <b-media-aside class="mr-50">
                    <b-avatar size="44" variant="light-warning">
                      <feather-icon size="24" icon="ClockIcon" />
                    </b-avatar>
                  </b-media-aside>
                  <b-media-body class="">
                    <h4 class="font-weight-bolder mb-0">{{ $t("Late") }}</h4>
                    <b-card-text class="mb-0">
                      {{ summaryObj.late }}
                    </b-card-text>
                  </b-media-body>
                </b-media>
              </b-card>
            </b-col>
            <b-col xl="2" md="4">
              <b-card body-class="pl-1">
                <b-media no-body>
                  <b-media-aside class="mr-50">
                    <b-avatar size="44" variant="light-primary">
                      <feather-icon size="24" icon="CloudIcon" />
                    </b-avatar>
                  </b-media-aside>
                  <b-media-body class="">
                    <h4 class="font-weight-bolder mb-0">
                      {{ $t("Holidays") }}
                    </h4>
                    <b-card-text class="mb-0">
                      {{ summaryObj.holidays }}
                    </b-card-text>
                  </b-media-body>
                </b-media>
              </b-card>
            </b-col>
            <b-col xl="2" md="4">
              <b-card body-class="pl-1">
                <b-media no-body>
                  <b-media-aside class="mr-50">
                    <b-avatar size="44" variant="light-danger">
                      <feather-icon size="24" icon="InfoIcon" />
                    </b-avatar>
                  </b-media-aside>
                  <b-media-body class="">
                    <h4 class="font-weight-bolder mb-0">
                      {{ $t("Suspended") }}
                    </h4>
                    <b-card-text class="mb-0">
                      {{ summaryObj.suspended }}
                    </b-card-text>
                  </b-media-body>
                </b-media>
              </b-card>
            </b-col>
          </b-row>
        </b-col>
        <b-col>
          <div v-if="!isHappiness" class="d-flex justify-content-between">
            <b-button
              variant="outline-secondary"
              class="mr-50"
              @click="showreport = false"
            >
              <feather-icon class="" icon="ArrowLeftIcon" />
              {{ $t("Back") }}
            </b-button>
          </div>
          <b-card class="mt-1">
            <b-row class="align-items-center">
              <!-- <b-col md="2">
                <b-form-group
                  label="Select type"
                  invalid-feedback="type is required."
                  ref="type"
                >
                  <v-select
                    ref="type"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :clearable="false"
                    v-model="report.type"
                    :options="types"
                    :reduce="(opt) => opt.value"
                    label="name"
                    @input="setType()"
                    placeholder="select type"
                  />
                </b-form-group>
              </b-col> -->
              <b-col md="2" v-if="showClasses">
                <b-form-group
                  :label="$t('Select ' + getLabel('Class'))"
                  :invalid-feedback="$t(getLabel('class') + ' is required')"
                  ref="class"
                >
                  <v-select
                    ref="class"
                    multiple
                    :closeOnSelect="false"
                    v-model="classID"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="classesOptions"
                    :clearable="false"
                    label="name"
                    :reduce="(val) => val.id"
                    @input="FillSections()"
                    :placeholder="$t('Select ' + getLabel('class'))"
                  />
                </b-form-group>
              </b-col>
              <b-col md="2" v-if="showClasses">
                <b-form-group
                  :label="$t('Select ' + getLabel('Section'))"
                  :invalid-feedback="$t(getLabel('section') + ' is required')"
                  ref="section"
                >
                  <v-select
                    multiple
                    :closeOnSelect="false"
                    v-model="secID"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="admittedSections"
                    :reduce="(val) => val.id"
                    :clearable="false"
                    @input="checkSections()"
                    ref="section"
                    label="section"
                    :placeholder="$t('Select ' + getLabel('section'))"
                  />
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group
                  :label="$t('Date')"
                  :invalid-feedback="$t('date is required')"
                  ref="date"
                >
                  <flat-pickr
                    ref="date"
                    :config="config"
                    v-model="report.dtFrom"
                    class="form-control"
                    :placeholder="$t('Select Date')"
                  />
                  <!-- @input="setDate()" -->
                </b-form-group>
              </b-col>
              <b-button
                class="ml-1"
                :disabled="gridLoading"
                variant="primary"
                @click="loadgrid()"
              >
                {{ $t("Show") }}
              </b-button>
              <b-button
                variant="success"
                class="ml-1"
                :disabled="saving"
                @click="save()"
              >
                {{ $t("Save") }}
              </b-button>
              <b-dropdown
                variant="success"
                class="ml-1"
                :disabled="saving || marking"
              >
                <template #button-content>
                  {{ $t("Mark Status") }}
                </template>
                <b-dropdown-item @click="openMarkStatus('holiday')">
                  {{ $t("Holiday") }}
                </b-dropdown-item>
                <b-dropdown-item @click="openMarkStatus('off')">
                  {{ $t("Off") }}
                </b-dropdown-item>
                <b-dropdown-item @click="openMarkStatus('EXL')">
                  {{ $t("Exam Leave (EXL)") }}
                </b-dropdown-item>
                <b-dropdown-item @click="openMarkStatus('STL')">
                  {{ $t("Study Leave (STL)") }}
                </b-dropdown-item>
              </b-dropdown>
              <!-- Send Notification Button -->
              <b-button
                v-if="!isHappiness"
                class="ml-1 btn-icon"
                variant="outline-primary"
                v-b-tooltip.hover.top
                :title="$t('Send Notification')"
                @click="checkPrint('notification')"
                :disabled="iNotification"
              >
                <b-spinner v-if="iNotification" small type="grow" />
                <feather-icon v-if="!iNotification" icon="BellIcon" />
              </b-button>
              <b-button
                variant="outline-danger"
                class="ml-1 btn-icon"
                v-b-tooltip.hover.top
                :title="$t('Delete')"
                @click="DeleteAtt()"
                :disabled="deleting"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
              <b-button
                v-if="!isHappiness"
                class="ml-1 btn-icon"
                variant="outline-primary"
                v-b-tooltip.hover.top
                :title="$t('Smart Attendance')"
                @click="showSmart()"
              >
                <feather-icon icon="CheckCircleIcon" />
              </b-button>
              <b-button
                class="ml-1 btn-icon"
                variant="outline-primary"
                v-b-tooltip.hover.top
                :title="$t('Reports')"
                @click="openReport()"
              >
                <feather-icon icon="BarChart2Icon" />
              </b-button>
              <b-button
                v-if="!isHappiness"
                class="ml-1 btn-icon"
                variant="outline-primary"
                v-b-tooltip.hover.top
                :title="$t('Messages')"
                @click="openMessage()"
              >
                <feather-icon icon="MailIcon" />
              </b-button>
              <b-button
                v-if="showDaysSettings"
                class="ml-1 btn-icon"
                variant="outline-primary"
                v-b-tooltip.hover.top
                title="Days Settings"
                @click="openDaysSettings()"
              >
                <feather-icon icon="CalendarIcon" />
              </b-button>
              <b-button
                class="ml-1 btn-icon"
                variant="outline-secondary"
                v-b-tooltip.hover.top
                :title="$t('Settings')"
                @click="showSettings()"
              >
                <!-- @click="openSett()" -->
                <feather-icon icon="SettingsIcon" />
              </b-button>
            </b-row>
            <b-row class="mt-1" v-if="showGrid">
              <b-col lg="10" md="9">
                <b-form-group :label="$t('Search')">
                  <b-form-input
                    v-model="searchQuery"
                    class="d-inline-block"
                    :placeholder="$t('Search by name or gr')"
                  />
                </b-form-group>
              </b-col>
              <b-col lg="2" md="3">
                <b-form-group :label="$t('Sort By')">
                  <v-select
                    v-model="sortBy"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="sortOptions"
                    :reduce="(val) => val.value"
                    label="text"
                    @input="sortData()"
                    :clearable="false"
                    :placeholder="$t('Select')"
                  />
                </b-form-group>
              </b-col>
              <b-col>
                <b-table
                  class="mt-1"
                  :busy="gridLoading"
                  show-empty
                  :items="filteredAtt"
                  :fields="fields"
                  responsive
                  hover
                  selectable
                  select-mode="single"
                  @row-selected="showDetail($event[0])"
                >
                  <template #table-busy>
                    <div class="d-flex justify-content-center mb-3 mt-3">
                      <b-spinner
                        style="width: 3rem; height: 3rem"
                        type="grow"
                        variant="primary"
                        label="Spinning"
                      ></b-spinner>
                    </div>
                  </template>

                  <template #head()="scope">
                    <div>
                      <span>{{
                        $t(
                          scope.label == "class"
                            ? getLabel(scope.label)
                            : scope.label
                        )
                      }}</span>
                    </div>
                  </template>

                  <template #cell(index)="data">
                    {{ gridData.indexOf(data.item) + 1 }}
                  </template>

                  <template #cell(cls)="data">
                    <b-badge variant="light-primary">
                      {{ data.item.cls }}
                    </b-badge>
                  </template>

                  <template #cell(checkin)="data">
                    <b-badge variant="light-primary" v-if="data.item.date">
                      <!-- @click="data.item.date = null" -->
                      {{
                        new Intl.DateTimeFormat("en-US", {
                          hour: "numeric",
                          minute: "numeric",
                        }).format(new Date(data.item.date))
                      }}
                    </b-badge>
                    <b-form-group v-else class="mb-0">
                      <b-input-group>
                        <flat-pickr
                          :config="{
                            enableTime: true,
                            noCalendar: true,
                            altFormat: 'h:i K',
                            altInput: true,
                            dateFormat: 'Y-m-d\\TH:i:S',
                            defaultHour: 8,
                          }"
                          v-model="filteredAtt[data.index].inputTimein"
                          class="form-control"
                        />
                        <b-input-group-append
                          is-text
                          @click.stop="
                            filteredAtt[data.index].inputTimein = null
                          "
                          v-b-tooltip.hover.top
                          :title="$t('Clear')"
                        >
                          <feather-icon
                            class="cursor-pointer"
                            size="16"
                            icon="XIcon"
                          />
                        </b-input-group-append>
                      </b-input-group>
                    </b-form-group>
                  </template>
                  <template #cell(status)="data">
                    <div class="d-flex align-items-center">
                      <b-button
                        pill
                        size="sm"
                        style="margin-inline: 2px"
                        :variant="
                          data.item.obj.status == 'P'
                            ? 'success'
                            : 'outline-success'
                        "
                        @click="changeAtt(data.index, 'P')"
                      >
                        P
                      </b-button>
                      <b-button
                        pill
                        size="sm"
                        style="margin-inline: 2px"
                        :variant="
                          data.item.obj.status == 'A'
                            ? 'danger'
                            : 'outline-danger'
                        "
                        @click="changeAtt(data.index, 'A')"
                      >
                        A
                      </b-button>
                      <b-button
                        pill
                        size="sm"
                        style="margin-inline: 2px"
                        :variant="
                          data.item.obj.status == 'L' ? 'info' : 'outline-info'
                        "
                        @click="changeAtt(data.index, 'L')"
                      >
                        L
                      </b-button>
                      <b-button
                        pill
                        size="sm"
                        style="margin-inline: 2px"
                        :variant="
                          data.item.obj.status.toLowerCase() == 'late'
                            ? 'warning'
                            : 'outline-warning'
                        "
                        @click="changeAtt(data.index, 'Late')"
                      >
                        Late
                      </b-button>
                      <b-button
                        v-if="isHappiness"
                        pill
                        size="sm"
                        style="margin-inline: 2px"
                        :variant="
                          data.item.obj.status.toLowerCase() == 'very late'
                            ? 'warning'
                            : 'outline-warning'
                        "
                        @click="changeAtt(data.index, 'Very Late')"
                      >
                        Very Late
                      </b-button>
                      <b-button
                        pill
                        size="sm"
                        style="margin-inline: 2px"
                        :variant="
                          data.item.obj.status == 'H' ? 'info' : 'outline-info'
                        "
                        @click="changeAtt(data.index, 'H')"
                      >
                        H
                      </b-button>
                      <b-button
                        pill
                        size="sm"
                        style="margin-inline: 2px"
                        :variant="
                          data.item.obj.status == 'Off'
                            ? 'secondary'
                            : 'outline-secondary'
                        "
                        @click="changeAtt(data.index, 'Off')"
                      >
                        Off
                      </b-button>
                      <b-button
                        pill
                        size="sm"
                        style="margin-inline: 2px"
                        v-b-tooltip.hover.top
                        title="Exam Leave"
                        :variant="
                          data.item.obj.status == 'EXL'
                            ? 'dark'
                            : 'outline-dark'
                        "
                        @click="changeAtt(data.index, 'EXL')"
                      >
                        EXL
                      </b-button>
                      <b-button
                        pill
                        size="sm"
                        style="margin-inline: 2px"
                        v-b-tooltip.hover.top
                        title="Study Leave"
                        :variant="
                          data.item.obj.status == 'STL'
                            ? 'primary'
                            : 'outline-primary'
                        "
                        @click="changeAtt(data.index, 'STL')"
                      >
                        STL
                      </b-button>
                    </div>
                  </template>

                  <template #cell(reason)="data">
                    <b-form-textarea
                      style="width: 200px"
                      rows="2"
                      size="sm"
                      v-model.trim="data.item.obj.meta_notes"
                      placeholder="Enter excused reason"
                      :disabled="data.item.obj.status != 'L'"
                    />
                  </template>
                </b-table>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </template>
    </b-row>

    <!-- attendance card -->
    <b-row align-h="center" v-else-if="dataLoading">
      <b-spinner
        class="m-5"
        style="width: 4rem; height: 4rem"
        type="grow"
        variant="primary"
        label="Spinning"
      ></b-spinner>
    </b-row>
    <div v-else>
      <b-row align-h="center">
        <b-col v-if="studentLoading" md="4">
          <b-card class="text-center p-1" style="position: relative">
            <b-spinner
              class="m-5"
              style="width: 4rem; height: 4rem"
              variant="primary"
              label="Spinning"
            ></b-spinner>
          </b-card>
        </b-col>
        <b-col lg="4" md="5" v-else>
          <b-card class="text-center p-1" style="position: relative">
            <!-- <feather-icon
              class="cursor-pointer"
              style="position: absolute; right: 15px; top: 15px"
              icon="SettingsIcon"
              size="22"
              v-b-tooltip.hover.right
              :title="$t('Settings')"
              @click="showSettings()"
            /> -->
            <b-img
              v-if="
                currentStd.std.picture == '' || currentStd.std.picture == null
              "
              class="round mb-1"
              src="https://cdn.cloudious.net/file-1677912450367-333289208.png"
              style="
                width: 100px;
                height: 100px;
                object-fit: cover;
                border-radius: 50%;
              "
              :class="{ 'set-picSize': picObj && picObj.valueBool }"
            />
            <b-img
              v-else
              class="round mb-1"
              :src="currentStd.std.picture"
              style="
                width: 100px;
                height: 100px;
                object-fit: cover;
                border-radius: 50%;
              "
              :class="{ 'set-picSize': picObj && picObj.valueBool }"
            />
            <h3 v-if="currentStd.std.name" class="mb-1">
              {{ currentStd.std.name }}
            </h3>
            <h3 v-else class="mb-1">-</h3>
            <h4 v-if="currentStd.std.cls" class="mb-50">
              {{ currentStd.std.cls }}
            </h4>
            <h4 v-else class="mb-50">-</h4>
            <h4 class="mb-50">
              {{ $t("GR") }} #
              <span v-if="currentStd.std.grNo">{{ currentStd.std.grNo }}</span>
              <span v-else>-</span>
            </h4>
            <h4 v-if="settings.showDues" class="mb-50">
              {{ $t("Dues") }}:
              <span v-if="currentStd.dues">{{ currentStd.dues }}</span>
              <span v-else>-</span>
            </h4>
            <h1 v-if="currentStd.time" class="mt-1">
              {{
                new Intl.DateTimeFormat("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                }).format(new Date(currentStd.time))
              }}
            </h1>

            <!-- <h4 class="text-danger font-weight-bolder">dues</h4> -->
            <b-row align-h="center">
              <b-form-input
                class="mt-1"
                style="width: 80%"
                id="mc-first-name"
                :placeholder="$t('Scan here')"
                ref="name"
                autofocus
                v-model="studentID"
                @keyup.enter="GetStudent()"
              />
            </b-row>
          </b-card>
        </b-col>
        <b-col lg="1" md="2" class="d-flex flex-wrap d-md-block">
          <b-card
            class="mb-1 mr-1 mr-md-0 rounded-circle"
            style="width: 45px; height: 45px"
            v-b-tooltip.hover.right="$t('Settings')"
            body-class="d-flex align-items-center justify-content-center p-50"
          >
            <feather-icon
              class="cursor-pointer"
              icon="SettingsIcon"
              size="22"
              @click="showSettings()"
            />
          </b-card>
          <b-card
            class="mb-1 mr-1 mr-md-0 rounded-circle"
            style="width: 45px; height: 45px"
            v-b-tooltip.hover.right="$t('Smart Attendance')"
            body-class="d-flex align-items-center justify-content-center p-50"
          >
            <feather-icon
              class="cursor-pointer"
              icon="CheckCircleIcon"
              size="22"
              @click="showSmart()"
            />
          </b-card>
          <b-card
            class="mb-1 mr-1 mr-md-0 rounded-circle"
            style="width: 45px; height: 45px"
            v-b-tooltip.hover.right="$t('Reports')"
            body-class="d-flex align-items-center justify-content-center p-50"
          >
            <feather-icon
              class="cursor-pointer"
              icon="BarChart2Icon"
              size="22"
              @click="openReport()"
            />
          </b-card>
          <b-card
            class="mb-1 mr-1 mr-md-0 rounded-circle"
            style="width: 45px; height: 45px"
            v-b-tooltip.hover.right="$t('Messages')"
            body-class="d-flex align-items-center justify-content-center p-50"
          >
            <feather-icon
              class="cursor-pointer"
              icon="MailIcon"
              size="22"
              @click="openMessage()"
            />
          </b-card>
        </b-col>
      </b-row>
      <b-row align-h="center">
        <b-col lg="4" md="5">
          <b-button variant="primary" block @click="openDetails()">
            {{ $t("Show Details") }}
          </b-button>
        </b-col>
        <b-col lg="1" md="2"></b-col>
      </b-row>
      <b-row v-if="this.settings.showRecent && this.filteredItems.length > 0">
        <b-col md="12">
          <h3>{{ $t("Recent Attendance") }}</h3>
        </b-col>
        <b-col md="3" v-for="card in filteredItems" :key="card">
          <b-card
            class="text-center shadow-sm"
            style="border-radius: 12px; overflow: hidden"
            :class="{
              'set-border': card.dues > 0,
            }"
          >
            <b-avatar
              v-if="card.std.picture == '' || card.std.picture == null"
              size="80"
              src="https://cdn.cloudious.net/file-1677912450367-333289208.png"
              class="mb-1 mt-1 shadow-sm"
              :class="{ 'border-danger': card.dues > 0 }"
            />
            <b-avatar
              v-else
              size="80"
              :src="card.std.picture"
              class="mb-1 mt-1 shadow-sm"
              :class="{ 'border-danger': card.dues > 0 }"
            />

            <h4 class="font-weight-bolder mb-50">{{ card.std.name }}</h4>

            <h6 class="text-muted mb-1">
              {{ card.std.cls }}
              <span class="mx-50">•</span>
              GR# {{ card.std.grNo }}
            </h6>

            <div
              class="d-flex justify-content-center flex-wrap align-items-center mb-1"
              style="gap: 5px"
            >
              <b-badge variant="light-primary" pill>
                <feather-icon icon="ClockIcon" size="12" class="mr-25" />
                {{
                  new Intl.DateTimeFormat("en-US", {
                    hour: "numeric",
                    minute: "numeric",
                  }).format(new Date(card.time))
                }}
              </b-badge>
              <b-badge
                v-if="card.std.status"
                :variant="
                  card.std.status.toLowerCase() == 'absent'
                    ? 'light-danger'
                    : 'light-success'
                "
                pill
                class="text-uppercase"
                style="letter-spacing: 0.5px"
              >
                {{ card.std.status }}
              </b-badge>
              <b-badge
                v-if="settings.showDues && card.dues > 0"
                variant="light-danger"
                pill
              >
                Dues: {{ card.dues }}
              </b-badge>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BAvatar,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BModal,
  BFormGroup,
  BForm,
  BMediaAside,
  BAside,
  BSpinner,
  BCardTitle,
  BCardText,
  BSidebar,
  VBToggle,
  BInputGroupPrepend,
  BInputGroupAppend,
  BInputGroup,
  BFormTextarea,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions, mapGetters } from "vuex";

import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";

import axios from "axios";

export default {
  components: {
    BFormTextarea,
    BMediaAside,
    BMedia,
    BCard,
    BRow,
    BImg,
    BAvatar,
    BBadge,
    BTable,
    BCol,
    BFormInput,
    BButton,
    BSidebar,
    BFormGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BModal,
    BForm,
    BPagination,
    BSpinner,
    BDropdown,
    BDropdownItem,
    vSelect,
    flatPickr,
    FullCalendar,
  },
  directives: {
    "b-tooltip": VBTooltip,
    "b-toggle": VBToggle,
    Ripple,
  },
  async created() {
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      if (this.isHappiness) {
        this.openDetails();
        this.fields.push({ label: "reason", key: "reason" });
      }
      this.classesOptions = [
        { name: "All " + this.getLabel("Classes"), id: 0 },
      ];
      this.admittedSections = [
        { section: "All " + this.getLabel("Sections"), id: 0 },
      ];
      this.repSecOptions = [
        { section: "All " + this.getLabel("Sections"), id: 0 },
      ];
      this.holiSecOptions = [
        { section: "All " + this.getLabel("Sections"), id: 0 },
      ];
      this.LoadClasses();
      this.LoadSettings();
      this.LoadDepartments();
      this.loadSessions();
      this.loadOther();
      this.loadOtherAll();
      this.loadSummary();
    }
    // this.LoadClasses();
    // this.LoadSettings();
    // this.LoadDepartments();
    // this.loadSessions();
    // this.loadOther();
    // this.loadSummary();
  },
  computed: {
    isHappiness() {
      return this.reportDomain == "myskoolhappinessfoundation";
    },
    filteredItems: function () {
      return this.items.slice(0, 12);
    },
    filteredAtt() {
      return this.gridData.filter((pro) => {
        return (
          pro.name.toLowerCase().match(this.searchQuery.toLowerCase()) ||
          pro.grNo.toLowerCase().match(this.searchQuery.toLowerCase())
        );
      });
    },
    showDaysSettings() {
      const s = this.daySettingObj;
      if (!s) return false;
      if (s.valueBool === true || s.valueBool === 1) return true;
      if (typeof s.valueBool === "string") {
        const t = s.valueBool.toLowerCase().trim();
        return t === "true" || t === "1";
      }
      return false;
    },
    studentHolidayHistoryRange() {
      const total = this.studentHolidayHistoryTotal || 0;
      if (!total) return { start: 0, end: 0 };
      const start =
        (this.studentHolidayHistoryPage - 1) *
          this.studentHolidayHistoryPageSize +
        1;
      const end = Math.min(
        this.studentHolidayHistoryPage * this.studentHolidayHistoryPageSize,
        total
      );
      return { start, end };
    },
    studentHolidayHistoryStatusFilterOptions() {
      const base = [{ label: this.$t("All types"), value: "" }];
      const fromApi = this.studentHolidayHistoryStatusOptions || [];
      if (fromApi.length) return base.concat(fromApi);
      return base.concat(this.defaultStudentHolidayHistoryStatusOptions);
    },
    showStudentHolidayHistoryCampusColumn() {
      return Number(this.getStudentHolidayHistoryCampusId()) === 0;
    },
    visibleStudentHolidayHistoryFields() {
      const fields = this.studentHolidayHistoryFields || [];
      if (this.showStudentHolidayHistoryCampusColumn) return fields;
      return fields.filter((f) => f.key !== "campusName");
    },
    holidaySummaryHasWarn() {
      return (Number(this.holidaySummary?.alreadyHoliday) || 0) > 0;
    },
    holidaySummaryTotalProcessed() {
      const s = this.holidaySummary || {};
      if (s.totalProcessed != null && s.totalProcessed !== "") {
        return s.totalProcessed;
      }
      return (
        (Number(s.created) || 0) +
        (Number(s.updated) || 0) +
        (Number(s.alreadyHoliday) || 0)
      );
    },
  },
  data() {
    let yList = [];
    for (let i = 2020; i <= 2040; i++) {
      yList.push(i);
    }
    return {
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      rights: {},
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        // mode: "range",
      },
      config2: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        mode: "range",
      },
      calendarOptions: {
        plugins: [dayGridPlugin],
        initialView: "dayGridMonth",
        headerToolbar: {
          start: "title",
          end: "",
        },
        events: [],
        eventResizableFromStart: true,
        dragScroll: true,
        dayMaxEvents: 2,
        // navLinks: true,
        eventClassNames({ event: e }) {
          const colorName =
            e.title == "P"
              ? "success"
              : e.title == "A" || e.title == "AA"
              ? "danger"
              : e.title == "L" || e.title == "H" || e.title == "SL"
              ? "info"
              : "warning";

          return [`text-${colorName}`, 'att-calendar-event-cell'];
        },
        eventClick: this.openEditAtt,
      },
      attMonth: new Date().getMonth() + 1,
      attYear: new Date().getFullYear(),
      currentObj: {
        status: "",
      },
      savingAtt: false,
      months: [
        { text: "January", value: 1 },
        { text: "February", value: 2 },
        { text: "March", value: 3 },
        { text: "April", value: 4 },
        { text: "May", value: 5 },
        { text: "June", value: 6 },
        { text: "July", value: 7 },
        { text: "August", value: 8 },
        { text: "September", value: 9 },
        { text: "October", value: 10 },
        { text: "November", value: 11 },
        { text: "December", value: 12 },
      ],
      years: yList,
      repDate: "",
      departments: [],
      request: false,
      sheetLoading: false,
      exporting: false,
      attendance: {
        picture: null,
        obj: {
          status: null,
        },
      },
      dataLoading: false,
      studentLoading: false,
      visibility: false,
      visibility2: false,
      visibility3: false,
      visibility4: false,
      visibility5: false,
      logoloading: "",
      searchQuery: "",
      sortOptions: [
        { text: "Name", value: "name" },
        { text: "GR#", value: "gr" },
      ],
      sortBy: this.$store.state.attSort,
      currentPage: 1,
      perPage: 30,
      totalRows: 0,
      report: {
        type: "",
        dtFrom: new Date().toJSON().split("T")[0],
        // dtTo: null,
        clsIDs: [],
        secIDs: [],
      },
      showreport: false,
      rangeDate: null,
      types: [
        { name: "Student", value: "student" },
        { name: "Staff", value: "staff" },
      ],
      statusOptions: [
        { name: "All", value: "" },
        { name: "Present", value: "P" },
        { name: "Absent", value: "A" },
        { name: "Leave", value: "L" },
        { name: "Late", value: "Late" },
      ],
      smartOptions: [
        { name: "Absent", value: "A" },
        { name: "Leave", value: "L" },
        { name: "Late", value: "Late" },
      ],
      showClasses: false,
      classes: [],
      classesOptions: [],
      admittedSections: [],

      classID: [0],
      secID: [0],

      gridData: [],
      showGrid: false,
      gridLoading: false,
      fields: [
        // { label: "profile", key: "profile" },
        { label: "S.no", key: "index" },
        { label: "gr no", key: "grNo" },
        { label: "name", key: "name" },
        { label: "Father name", key: "father_name" },
        { label: "class", key: "cls" },
        { label: "status", key: "status" },
      ],
      items: [],
      myObj: {
        picture: null,
        cls: null,
        grNo: null,
        name: null,
      },
      currentStd: {
        std: {
          picture: null,
          cls: null,
          grNo: null,
          name: null,
        },
      },
      settings: {
        id: 0,
        campusID: this.$store.state.userData.cId,
        notifyArrival: false,
        notifyDeparture: false,
        showDues: false,
        showRecent: false,
        autoHide: false,
        hideAfterSeconds: 5,
        attendanceMode: "",
      },
      msgObj: {
        dt: "",
        clsIDs: [0],
        secIDs: [0],
        status: "",
      },
      msgSecOptions: [{ section: "All Sections", id: 0 }],
      msgClass: [0],
      msgSection: [0],

      repObj: {
        type: "",
        dtFrom: "",
        clsIDs: [0],
        secIDs: [0],
        status: [],
      },
      repSecOptions: [],
      repClass: [0],
      repSection: [0],
      reportType: "",
      reportOptions: [
        { text: "General", value: "" },
        { text: "Summary", value: "summary" },
        { text: "Student wise", value: "student-wise" },
      ],
      detailObj: {},
      timeout: 10000,
      studentID: null,
      saving: false,
      deleting: false,
      smartObj: {
        status: "",
        dt: null,
        dtFrom: null,
        dtTo: null,
        gr: "",
      },
      sessions: [],
      sessionID: 0,
      picObj: null,
      checkInObj: null,
      checkOutObj: null,
      countObj: {
        present: 0,
        absent: 0,
        leave: 0,
        late: 0,
      },
      summaryObj: {
        present: 0,
        absent: 0,
        leave: 0,
        late: 0,
        holidays: 0,
        suspended: 0,
      },
      summLoading: false,
      showDetails: false,
      detailLoading: false,
      detailItems: [],
      detailFields: [
        { label: "date", key: "date" },
        { label: "status", key: "status" },
        { label: "time", key: "time" },
      ],
      savingCh: false,
      holiObj: {
        dtFrom: "",
        dtTo: "",
        departmentIDs: [0],
        clsIDs: [0],
        secIDs: [0],
        statusType: "holiday",
        reason: "",
      },
      markStatusOptions: [
        { label: "Holiday", value: "holiday" },
        { label: "Off", value: "off" },
        { label: "Exam Leave (EXL)", value: "EXL" },
        { label: "Study Leave (STL)", value: "STL" },
      ],
      holiSecOptions: [],
      visibility6: false,
      marking: false,
      iNotification: false,
      holidaySummaryVisible: false,
      holidaySummary: null,
      studentHolidayHistoryVisible: false,
      studentHolidayHistoryLoading: false,
      studentHolidayHistoryDeletingId: null,
      studentHolidayHistoryItems: [],
      studentHolidayHistoryPage: 1,
      studentHolidayHistoryPageSize: 50,
      studentHolidayHistoryTotal: 0,
      studentHolidayHistoryFilters: {
        dtFrom: "",
        dtTo: "",
        status: "",
      },
      studentHolidayHistoryStatusOptions: [],
      defaultStudentHolidayHistoryStatusOptions: [
        { label: "Holiday (H)", value: "H" },
        { label: "Exam Leave (EXL)", value: "EXL" },
        { label: "Study Leave (STL)", value: "STL" },
        { label: "Off", value: "Off" },
      ],
      studentHolidayHistoryFields: [
        { key: "campusName", label: "Campus", thClass: "hh-th", tdClass: "hh-td" },
        { key: "status", label: "Status", thClass: "hh-th", tdClass: "hh-td" },
        { key: "dtFrom", label: "From", thClass: "hh-th", tdClass: "hh-td" },
        { key: "dtTo", label: "To", thClass: "hh-th", tdClass: "hh-td" },
        { key: "reason", label: "Reason", thClass: "hh-th", tdClass: "hh-td" },
        { key: "clsIDs", label: "Classes", thClass: "hh-th", tdClass: "hh-td" },
        { key: "secIDs", label: "Sections", thClass: "hh-th", tdClass: "hh-td" },
        {
          key: "createdByName",
          label: "Created By",
          thClass: "hh-th",
          tdClass: "hh-td",
        },
        {
          key: "createdAt",
          label: "Created At",
          thClass: "hh-th",
          tdClass: "hh-td",
        },
        {
          key: "actions",
          label: "",
          thClass: "hh-th hh-th-actions",
          tdClass: "hh-td hh-td-actions",
        },
      ],

      // Student Availability / Days Settings
      daySettingObj: null,
      visibilityDays: false,
      availLoading: false,
      availSaving: false,
      availDeleting: null,
      availList: [],
      availTotal: 0,
      availPage: 1,
      availPageSize: 10,
      availSecOptions: [{ section: "All Sections", id: 0 }],
      weekdayOptions: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      availObj: {
        id: 0,
        classIDs: [0],
        sectionIDs: [0],
        days: [],
        status: "Off",
      },
    };
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    getVariant(title) {
      const vt =
        title == "P"
          ? "success"
          : title == "A" || title == "AA"
          ? "danger"
          : title == "L" || title == "H" || title == "SL"
          ? "info"
          : "warning";

      return vt;
    },

    getLabel(key) {
      const isHappiness = this.isHappiness;
      const map = {
        class: isHappiness ? "group" : "class",
        classes: isHappiness ? "groups" : "classes",
        section: isHappiness ? "level" : "section",
        sections: isHappiness ? "levels" : "sections",
      };
      const result = map[key.toLowerCase()] ?? key.toLowerCase();
      const isCapitalized =
        key[0] === key[0].toUpperCase() && key[0] !== key[0].toLowerCase();
      return isCapitalized
        ? result.charAt(0).toUpperCase() + result.slice(1)
        : result;
    },

    openMarkStatus(statusType = "holiday") {
      this.holiObj = {
        dtFrom: "",
        dtTo: "",
        departmentIDs: [0],
        clsIDs: [0],
        secIDs: [0],
        statusType,
        reason: "",
      };
      this.holiSecOptions = [
        { section: "All " + this.getLabel("Sections"), id: 0 },
      ];
      this.visibility6 = true;

      const refs = [
        "hol_class",
        "hol_section",
        "hol_dateF",
        "hol_dateT",
        "hol_status",
      ];
      refs.forEach((refName) => {
        const elem = this.$refs[refName];
        if (elem) elem.state = undefined;
      });
    },

    setHoliDept() {
      if (this.holiObj.departmentIDs.at(-1) == 0) {
        this.holiObj.departmentIDs = [0];
      } else {
        this.holiObj.departmentIDs = this.holiObj.departmentIDs.filter(
          (el) => el != 0
        );
      }
    },
    setHoliClass() {
      if (this.holiObj.clsIDs.at(-1) == 0) {
        this.holiObj.clsIDs = [0];
        this.holiSecOptions = [
          { section: "All " + this.getLabel("Sections"), id: 0 },
        ];
        this.holiObj.secIDs = [0];
      } else {
        this.holiObj.clsIDs = this.holiObj.clsIDs.filter((el) => el != 0);

        if (this.holiObj.clsIDs.length == 1) {
          let obj = this.classes.find(
            (el) => el.cls.id == this.holiObj.clsIDs[0]
          );
          if (obj && obj.sections.length > 0) {
            this.holiSecOptions = [...obj.sections];
            this.holiSecOptions.unshift({
              section: "All " + this.getLabel("Sections"),
              id: 0,
            });
          } else {
            this.holiSecOptions = [
              { section: "All " + this.getLabel("Sections"), id: 0 },
            ];
          }
          this.holiObj.secIDs = [0];
        } else {
          this.holiSecOptions = [
            { section: "All " + this.getLabel("Sections"), id: 0 },
          ];
          this.holiObj.secIDs = [0];
        }
      }
    },
    setHoliSections() {
      if (this.holiObj.secIDs.at(-1) == 0) {
        this.holiObj.secIDs = [0];
      } else {
        this.holiObj.secIDs = this.holiObj.secIDs.filter((el) => el != 0);
      }
    },
    async markAttendanceStatus() {
      if (
        !this.holiObj.statusType ||
        !this.holiObj.dtFrom ||
        !this.holiObj.dtTo ||
        this.holiObj.clsIDs.length == 0 ||
        this.holiObj.secIDs.length == 0
      ) {
        return this.$bvToast.toast(
          this.$t("Please select all the required fields"),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      }
      if (this.holiObj.dtFrom > this.holiObj.dtTo) {
        return this.$bvToast.toast("Please select the date correctly!", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }

      this.marking = true;

      const cls = this.holiObj.clsIDs[0] == 0 ? [] : this.holiObj.clsIDs;
      const sec = this.holiObj.secIDs[0] == 0 ? [] : this.holiObj.secIDs;
      const statusType = this.holiObj.statusType;
      const isOff = statusType === "off";
      const usesMarkHoliday = !isOff;

      const defaultReasonByStatus = {
        holiday: "Holiday",
        EXL: "Exam Leave",
        STL: "Study Leave",
      };
      const successMessageByStatus = {
        holiday: "Attendance marked as holiday",
        EXL: "Exam leave marked successfully!",
        STL: "Study leave marked successfully!",
        off: "Off attendance marked successfully!",
      };

      const payload = {
        dtFrom: this.holiObj.dtFrom,
        dtTo: this.holiObj.dtTo,
        clsIDs: cls,
        secIDs: sec,
        userID: this.$store.state.userData.userID,
      };
      if (usesMarkHoliday) {
        payload.reason =
          (this.holiObj.reason || "").trim() ||
          defaultReasonByStatus[statusType] ||
          "Holiday";
        if (statusType === "EXL" || statusType === "STL") {
          payload.status = statusType;
        }
      }

      const endpoint = usesMarkHoliday
        ? "Attendance/MarkHolidayByClass"
        : "Attendance/MarkSundayByClass";
      const successMessage =
        successMessageByStatus[statusType] ||
        successMessageByStatus.holiday;

      const postUrl =
        this.$store.state.domain +
        endpoint +
        "?db=" +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId;

      if (usesMarkHoliday) {
        const result = await this.post({
          url: postUrl,
          body: payload,
          context: this,
          token: this.$store.state.userData.token,
          message: successMessage,
          returnData: true,
        });
        this.marking = false;
        if (result) {
          this.visibility6 = false;
          this.holidaySummary = result;
          this.holidaySummaryVisible = true;
          if (this.showGrid) {
            this.loadgrid();
          }
        }
        return;
      }

      const status = await this.post({
        url: postUrl,
        body: payload,
        context: this,
        token: this.$store.state.userData.token,
        message: successMessage,
        showError: true,
      });
      this.marking = false;
      if (status) {
        this.visibility6 = false;
        if (this.showGrid) {
          this.loadgrid();
        }
      }
    },

    getStudentHolidayHistoryCampusId() {
      if (Number(this.$store.state.userData.cId) === 1) return 0;
      return this.$store.state.userData.cId;
    },

    mapMarkStatusToHistoryFilter(statusType) {
      if (statusType === "holiday") return "H";
      if (statusType === "off") return "Off";
      if (statusType === "EXL" || statusType === "STL") return statusType;
      return "";
    },

    holidayHistoryStatusLabel(code) {
      const key = String(code || "").trim();
      if (!key) return "—";
      const map = {
        H: this.$t("Holiday"),
        EXL: this.$t("Exam Leave"),
        STL: this.$t("Study Leave"),
        Off: this.$t("Off"),
      };
      return map[key] || key;
    },

    holidayHistoryStatusVariant(code) {
      const key = String(code || "")
        .trim()
        .toUpperCase();
      if (key === "H") return "info";
      if (key === "EXL") return "dark";
      if (key === "STL") return "primary";
      if (key === "OFF") return "secondary";
      return "light";
    },

    resolveHolidayHistoryRowStatus(row) {
      if (!row) return "";
      return row.leaveStatus || row.status || row.eventType || "";
    },

    holidayHistoryRowId(row) {
      if (!row) return null;
      if (row.calendarId != null && row.calendarId !== "") {
        return row.calendarId;
      }
      if (row.calendarID != null && row.calendarID !== "") {
        return row.calendarID;
      }
      return row.id != null ? row.id : null;
    },

    getHolidayHistoryDeleteCampusId(row) {
      const rowCampus = row && row.campusID;
      if (rowCampus != null && Number(rowCampus) > 0) {
        return Number(rowCampus);
      }
      const histCampus = this.getStudentHolidayHistoryCampusId();
      if (histCampus != null && Number(histCampus) > 0) {
        return Number(histCampus);
      }
      return this.$store.state.userData.cId;
    },

    formatHolidayHistoryCampus(row) {
      if (!row) return "—";
      if (row.branch) return row.branch;
      if (row.campusName) return row.campusName;
      if (Number(row.campusID) === 0) {
        return this.$t("All Campuses");
      }
      return "—";
    },

    extractHolidayHistoryStatusOptions(res) {
      if (res == null || typeof res !== "object") return [];
      const payload =
        res.data && typeof res.data === "object" && !Array.isArray(res.data)
          ? res.data
          : res;
      const raw = payload.statusOptions || res.statusOptions;
      if (!Array.isArray(raw) || !raw.length) return [];
      return raw
        .map((item) => {
          if (item == null) return null;
          if (typeof item === "string" || typeof item === "number") {
            const value = String(item).trim();
            if (!value) return null;
            return {
              label: this.holidayHistoryStatusLabel(value) + " (" + value + ")",
              value,
            };
          }
          if (typeof item === "object") {
            const value = String(
              item.value != null
                ? item.value
                : item.code != null
                ? item.code
                : item.status != null
                ? item.status
                : item.id != null
                ? item.id
                : ""
            ).trim();
            if (!value || value === "[object Object]") return null;
            const labelRaw = item.label || item.text || item.name || item.title;
            const label =
              labelRaw && typeof labelRaw !== "object"
                ? String(labelRaw)
                : this.holidayHistoryStatusLabel(value) + " (" + value + ")";
            return { label, value };
          }
          return null;
        })
        .filter(Boolean);
    },

    openStudentHolidayHistory() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      this.studentHolidayHistoryFilters = {
        dtFrom: this.holiObj.dtFrom || `${year}-${month}-01`,
        dtTo:
          this.holiObj.dtTo ||
          `${year}-${month}-${String(lastDay).padStart(2, "0")}`,
        status: this.mapMarkStatusToHistoryFilter(this.holiObj.statusType),
      };
      this.studentHolidayHistoryVisible = true;
      this.loadStudentHolidayHistory(1);
    },

    resetStudentHolidayHistoryFilters() {
      this.studentHolidayHistoryFilters = {
        dtFrom: "",
        dtTo: "",
        status: "",
      };
      this.loadStudentHolidayHistory(1);
    },

    normalizeStudentHolidayHistoryResponse(res) {
      if (res == null) return { items: [], total: 0, page: 1 };
      if (Array.isArray(res)) {
        return { items: res, total: res.length, page: 1 };
      }
      if (typeof res !== "object") return { items: [], total: 0, page: 1 };
      const payload = res.data && !Array.isArray(res.data) ? res.data : res;
      const items = Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload.items)
        ? payload.items
        : Array.isArray(res.data)
        ? res.data
        : [];
      const total =
        Number(
          payload.filteredTotal ??
            payload.totalCount ??
            payload.totalRecords ??
            payload.total ??
            res.filteredTotal ??
            res.totalCount ??
            res.total ??
            items.length
        ) || 0;
      const page = Number(payload.page ?? res.page ?? 1) || 1;
      return { items, total, page };
    },

    async loadStudentHolidayHistory(page = 1) {
      this.studentHolidayHistoryPage = Math.max(1, Number(page) || 1);
      this.studentHolidayHistoryLoading = true;
      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: String(this.getStudentHolidayHistoryCampusId()),
          page: String(this.studentHolidayHistoryPage),
          pageSize: String(this.studentHolidayHistoryPageSize),
        });
        if (this.studentHolidayHistoryFilters.dtFrom) {
          params.set("dtFrom", this.studentHolidayHistoryFilters.dtFrom);
        }
        if (this.studentHolidayHistoryFilters.dtTo) {
          params.set("dtTo", this.studentHolidayHistoryFilters.dtTo);
        }
        if (this.studentHolidayHistoryFilters.status) {
          params.set(
            "status",
            String(this.studentHolidayHistoryFilters.status)
          );
        }
        const raw = await this.get({
          url:
            this.$store.state.domain +
            "Attendance/StudentHolidayHistory?" +
            params.toString(),
          token: this.$store.state.userData.token,
          dashboard: true,
        });
        const normalized = this.normalizeStudentHolidayHistoryResponse(raw);
        const statusOpts = this.extractHolidayHistoryStatusOptions(raw);
        if (statusOpts.length) {
          this.studentHolidayHistoryStatusOptions = statusOpts;
        }
        this.studentHolidayHistoryItems = normalized.items;
        this.studentHolidayHistoryTotal = normalized.total;
        if (normalized.page > 0) {
          this.studentHolidayHistoryPage = normalized.page;
        }
      } catch (e) {
        console.error("loadStudentHolidayHistory:", e);
        this.studentHolidayHistoryItems = [];
        this.studentHolidayHistoryTotal = 0;
      } finally {
        this.studentHolidayHistoryLoading = false;
      }
    },

    formatStudentHolidayDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
      return d.toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },

    formatStudentHolidayDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatStudentHolidayIdList(value, allLabel) {
      if (value == null || value === "") return allLabel;
      if (Array.isArray(value)) {
        if (!value.length || (value.length === 1 && Number(value[0]) === 0)) {
          return allLabel;
        }
        return value.join(", ");
      }
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed || trimmed === "0" || trimmed === "[]") return allLabel;
        return trimmed;
      }
      return String(value);
    },

    formatStudentHolidayClasses(value) {
      return this.formatStudentHolidayIdList(
        value,
        this.$t("All") + " " + this.getLabel("Classes")
      );
    },

    formatStudentHolidaySections(value) {
      return this.formatStudentHolidayIdList(
        value,
        this.$t("All") + " " + this.getLabel("Sections")
      );
    },

    async deleteStudentHolidayHistory(row) {
      const calendarId = this.holidayHistoryRowId(row);
      if (!calendarId) return;
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t(
          "This will remove the holiday calendar entry and related holiday attendance."
        ),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });
      if (!result.isConfirmed) return;

      this.studentHolidayHistoryDeletingId = calendarId;
      try {
        const cID = this.getHolidayHistoryDeleteCampusId(row);
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: String(cID),
          id: String(calendarId),
          userID: String(this.$store.state.userData.userID || 0),
        });
        const status = await this.post({
          url:
            this.$store.state.domain +
            "Attendance/DeleteStudentHoliday?" +
            params.toString(),
          body: null,
          context: this,
          token: this.$store.state.userData.token,
          message: "Deleted successfully.",
        });
        if (status) {
          await this.loadStudentHolidayHistory(this.studentHolidayHistoryPage);
          if (this.showGrid) this.loadgrid();
        }
      } catch (e) {
        console.error("deleteStudentHolidayHistory:", e);
      } finally {
        this.studentHolidayHistoryDeletingId = null;
      }
    },

    openSett() {
      this.visibility5 = true;
    },

    openDaysSettings() {
      this.resetAvailForm();
      this.availPage = 1;
      this.availList = [];
      this.availTotal = 0;
      this.availLoading = true;
      this.visibilityDays = true;
    },

    resetAvailForm() {
      this.availObj = {
        id: 0,
        classIDs: [0],
        sectionIDs: [0],
        days: [],
        status: "Off",
      };
      this.availSecOptions = [{ section: "All Sections", id: 0 }];
    },

    joinAvailIds(ids) {
      if (!Array.isArray(ids) || !ids.length || ids.includes(0)) return "0";
      return ids.filter((id) => id !== 0).join(",");
    },

    parseAvailIds(value) {
      if (value === null || value === undefined || value === "") return [0];
      if (Array.isArray(value)) {
        const nums = value.map((v) => Number(v)).filter((v) => !Number.isNaN(v));
        return nums.length ? nums : [0];
      }
      const parts = String(value)
        .split(",")
        .map((part) => Number(String(part).trim()))
        .filter((n) => !Number.isNaN(n));
      if (!parts.length || parts.includes(0)) return [0];
      return parts;
    },

    setAvailClass() {
      if (this.availObj.classIDs.at(-1) == 0) {
        this.availObj.classIDs = [0];
        this.availSecOptions = [{ section: "All Sections", id: 0 }];
        this.availObj.sectionIDs = [0];
      } else {
        this.availObj.classIDs = this.availObj.classIDs.filter((el) => el != 0);

        if (this.availObj.classIDs.length == 1) {
          let obj = this.classes.find(
            (el) => el.cls.id == this.availObj.classIDs[0]
          );
          if (obj && obj.sections.length > 0) {
            this.availSecOptions = [...obj.sections];
            this.availSecOptions.unshift({ section: "All Sections", id: 0 });
          } else {
            this.availSecOptions = [{ section: "All Sections", id: 0 }];
          }
          this.availObj.sectionIDs = [0];
        } else {
          this.availSecOptions = [{ section: "All Sections", id: 0 }];
          this.availObj.sectionIDs = [0];
        }
      }
    },

    setAvailSections() {
      if (this.availObj.sectionIDs.at(-1) == 0) {
        this.availObj.sectionIDs = [0];
      } else {
        this.availObj.sectionIDs = this.availObj.sectionIDs.filter(
          (el) => el != 0
        );
      }
    },

    formatAvailClasses(item) {
      const ids = this.parseAvailIds(item.classIDs || item.classIds);
      if (ids.includes(0)) return "All Classes";
      const names = ids
        .map((id) => {
          const fromOptions = (this.classesOptions || []).find(
            (c) => c && c.id == id
          );
          if (fromOptions && fromOptions.name) return fromOptions.name;

          const fromClasses = (this.classes || []).find(
            (c) => c && c.cls && c.cls.id == id
          );
          if (fromClasses && fromClasses.cls && fromClasses.cls.name) {
            return fromClasses.cls.name;
          }

          return `Class #${id}`;
        })
        .filter(Boolean);
      return names.length ? names.join(", ") : "—";
    },

    formatAvailSections(item) {
      const ids = this.parseAvailIds(item.sectionIDs || item.sectionIds);
      if (ids.includes(0)) return "All Sections";

      const names = ids.map((id) => {
        for (const cls of this.classes || []) {
          const sections = (cls && cls.sections) || [];
          const found = sections.find((s) => s && s.id == id);
          if (found) return found.section || found.name || `Section #${id}`;
        }
        return `Section #${id}`;
      });

      return names.join(", ");
    },

    splitAvailDays(days) {
      if (!days) return [];
      return String(days)
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);
    },

    async loadStudentAvailability() {
      this.availLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "StudentAvailability/GetByPagination?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&pageNumber=" +
            this.availPage +
            "&pageSize=" +
            this.availPageSize,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        let rows = [];
        let total = 0;
        const payload = res && res.data !== undefined ? res.data : res;

        if (Array.isArray(payload)) {
          rows = payload;
          total = payload.length;
        } else if (payload && Array.isArray(payload.data)) {
          rows = payload.data;
          total = Number(
            payload.count ||
              payload.totalCount ||
              payload.total ||
              (res && (res.count || res.totalCount)) ||
              rows.length
          );
        } else if (payload && Array.isArray(payload.items)) {
          rows = payload.items;
          total = Number(
            payload.count || payload.totalCount || payload.total || rows.length
          );
        } else if (payload && Array.isArray(payload.list)) {
          rows = payload.list;
          total = Number(
            payload.count || payload.totalCount || payload.total || rows.length
          );
        }

        this.availList = rows.map((row) => ({
          ...row,
          isActive: row.isActive !== false && row.isActive !== 0,
        }));
        this.availTotal = total;
      } catch (e) {
        console.error("StudentAvailability load failed:", e);
        this.availList = [];
        this.availTotal = 0;
      } finally {
        this.availLoading = false;
      }
    },

    changeAvailPage(page) {
      this.availPage = page;
      this.loadStudentAvailability();
    },

    editStudentAvailability(item) {
      const classIDs = this.parseAvailIds(item.classIDs || item.classIds);
      const sectionIDs = this.parseAvailIds(item.sectionIDs || item.sectionIds);
      const days = String(item.days || "")
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);

      this.availObj = {
        id: Number(item.id) || 0,
        classIDs: classIDs.length ? classIDs : [0],
        sectionIDs: sectionIDs.length ? sectionIDs : [0],
        days,
        status: "Off",
      };

      if (this.availObj.classIDs.length === 1 && this.availObj.classIDs[0] !== 0) {
        const obj = this.classes.find(
          (el) => el.cls.id == this.availObj.classIDs[0]
        );
        if (obj && obj.sections.length > 0) {
          this.availSecOptions = [
            { section: "All Sections", id: 0 },
            ...obj.sections,
          ];
        } else {
          this.availSecOptions = [{ section: "All Sections", id: 0 }];
        }
      } else {
        this.availSecOptions = [{ section: "All Sections", id: 0 }];
      }
    },

    async saveStudentAvailability() {
      if (!this.availObj.classIDs.length) {
        return this.$bvToast.toast("Please select class", {
          title: "Error!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
      if (!this.availObj.sectionIDs.length) {
        return this.$bvToast.toast("Please select section", {
          title: "Error!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
      if (!this.availObj.days.length) {
        return this.$bvToast.toast("Please select days", {
          title: "Error!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }

      this.availSaving = true;
      try {
        const body = {
          id: Number(this.availObj.id) || 0,
          campusID: Number(this.$store.state.userData.cId) || 0,
          classIDs: this.joinAvailIds(this.availObj.classIDs),
          sectionIDs: this.joinAvailIds(this.availObj.sectionIDs),
          days: this.availObj.days.join(","),
          status: "Off",
        };

        const status = await this.post({
          url:
            this.$store.state.domain +
            "StudentAvailability/Save?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&userID=" +
            this.$store.state.userData.userID,
          body,
          message:
            body.id > 0
              ? "Days setting updated successfully."
              : "Days setting saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });

        if (status) {
          this.resetAvailForm();
          this.loadStudentAvailability();
        }
      } finally {
        this.availSaving = false;
      }
    },

    async deleteStudentAvailability(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        "Are you sure you want to delete this days setting?",
        {
          title: "Confirm Delete",
          okTitle: "Delete",
          cancelTitle: "Cancel",
          okVariant: "danger",
          centered: true,
        }
      );
      if (!ok) return;

      this.availDeleting = item.id;
      try {
        const status = await this.delete({
          url:
            this.$store.state.domain +
            "StudentAvailability/Delete?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&id=" +
            item.id,
          message: "Days setting deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          if (this.availObj.id === item.id) this.resetAvailForm();
          this.loadStudentAvailability();
        }
      } finally {
        this.availDeleting = null;
      }
    },

    async loadOtherAll() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettingsAll?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["student_day_setting"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      this.daySettingObj =
        (res || []).find((el) => el.key == "student_day_setting") || null;
    },

    async saveChecks() {
      // console.log(this.checkInObj, this.checkOutObj);
      this.savingCh = true;
      var status = await this.post({
        url:
          this.$store.state.domain +
          "Settings/SaveNew?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: [this.checkInObj, this.checkOutObj],
        message: "Settings saved successfully.",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.savingCh = false;
      if (status) {
        this.loadOther();
        this.visibility5 = false;
      }
    },

    async loadSummary() {
      this.summLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "Staff/LoadAttendanceByDate?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&dt=" +
          this.report.dtFrom +
          "&role=student",
        token: this.$store.state.userData.token,
        dashboard: true,
      };
      let res = await this.get(obj);
      this.summaryObj = res.summary;
      // console.log(this.summaryObj);
      this.summLoading = false;
    },
    showDetail(item) {
      this.detailObj = item;
      this.showDetails = true;

      this.attMonth = new Date().getMonth() + 1;
      this.attYear = new Date().getFullYear();

      this.loadDetails("");
    },
    async loadDetails(val) {
      this.detailLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "Students/LoadAttendance?db=" +
          this.$store.state.userData.db +
          "&sID=" +
          this.detailObj.sID +
          "&duration=" +
          val +
          "&month=" +
          this.attMonth +
          "&year=" +
          this.attYear,
        token: this.$store.state.userData.token,
        dashboard: true,
      };
      let res = await this.get(obj);
      this.detailItems = res.data;

      if (this.detailItems.length > 0) {
        this.calendarOptions.events = [];
        this.calendarOptions.events = this.detailItems.map((el) => ({
          ...el,
          title: el.status,
          start: el.date,
          allDay: true
        }));
      } else {
        this.calendarOptions.events = [];
      }
      this.countObj = res.summary;
      // console.log("res:", res);
      this.detailLoading = false;

      this.moveCalendar();
    },
    moveCalendar() {
      // console.log(this.attYear, this.attMonth);
      let dt = `${this.attYear}-${this.attMonth
        .toString()
        .padStart(2, "0")}-01`;

      this.$nextTick(() => {
        var elem = this.$refs.refCalendar.getApi();
        elem.gotoDate(dt);
      });
    },
    openEditAtt({ event: clickedEvent }) {
      console.log(clickedEvent); 
      const { id, extendedProps, start, startStr } = clickedEvent;
      // let dt = `${start.getFullYear()}-${(start.getMonth() + 1)
      //   .toString()
      //   .padStart(2, "0")}-${start.getDate().toString().padStart(2, "0")}`;
      this.currentObj = { ...extendedProps, id: parseInt(id), date: startStr };
      this.$bvModal.show("modal-att");
    },
    async saveAtt() {
      // console.log(this.currentObj);
      this.savingAtt = true;
      var status = await this.put({
        url:
          this.$store.state.domain +
          "Attendance/" +
          this.currentObj.id +
          "?db=" +
          this.$store.state.userData.db,
        message: "Attendance updated successfully.",
        context: this,
        body: this.currentObj,
        token: this.$store.state.userData.token,
      });
      this.savingAtt = false;
      if (status) {
        this.loadDetails("");
        this.$bvModal.hide("modal-att");
      }
    },

    sortData() {
      this.$store.commit("setAttSort", this.sortBy);

      if (this.sortBy == "gr") {
        this.gridData = this.gridData.sort((a, b) =>
          a.grNo
            .toLowerCase()
            .localeCompare(b.grNo.toLowerCase(), "en", { numeric: true })
        );
      } else {
        this.gridData = this.gridData.sort((a, b) =>
          a.name
            .toLowerCase()
            .localeCompare(b.name.toLowerCase(), "en", { numeric: true })
        );
      }
    },

    async loadOther() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["picture_size"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      // "check_in", "check_out"
      // console.log(res);
      this.picObj = res.find((el) => el.key == "picture_size");
      // console.log(this.picObj);

      // let a = res.find((el) => el.key == "check_in");
      // if (!a) {
      //   this.checkInObj = {
      //     id: 0,
      //     campusID: this.$store.state.userData.cId,
      //     key: "check_in",
      //     value: "check_in",
      //     valueBool: false,
      //     valueNumber: null,
      //   };
      // } else this.checkInObj = a;

      // let b = res.find((el) => el.key == "check_out");
      // if (!b) {
      //   this.checkOutObj = {
      //     id: 0,
      //     campusID: this.$store.state.userData.cId,
      //     key: "check_out",
      //     value: "check_out",
      //     valueBool: false,
      //     valueNumber: null,
      //   };
      // } else this.checkOutObj = b;
      // // console.log(this.checkInObj, this.checkOutObj);
      // let obj = this.fields.find((el) => el.label == "checkin");
      // if (this.checkInObj.id > 0 && !this.checkInObj.valueBool && obj) {
      //   this.fields = this.fields.filter((el) => el.label != "checkin");
      // }
      // if (this.checkInObj.id > 0 && this.checkInObj.valueBool && !obj) {
      //   this.fields.splice(4, 0, { label: "checkin", key: "checkin" });
      // }
    },

    async loadSessions() {
      var obj = {
        url:
          this.$store.state.domain +
          "Sessions?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.sessions = await this.get(obj);
      let cc = this.sessions.find((el) => el.isCurrentSession);
      this.sessionID = cc ? cc.id : 0;
    },
    changeAtt(ind, val) {
      this.filteredAtt[ind].obj.status = val;
    },
    async save() {
      if (!this.report.dtFrom) {
        this.$bvToast.toast("Please select the date", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } else if (this.gridData.length != 0) {
        this.saving = true;
        // this.gridData.forEach((el) => {
        //   el.date = !el.inputTimein
        //     ? this.report.dtFrom
        //     : `${this.report.dtFrom}T${el.inputTimein.split("T")[1]}`;
        // });
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Attendance/SaveWeb?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: this.gridData,
          message: "Attendance saved successfully!",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.saving = false;
        if (status) {
          this.loadgrid();
          // this.loadSummary();
        }
      }
    },
    openDetails() {
      this.showreport = true;
      this.report.type = "student";
      this.showClasses = true;
    },
    setType(val) {
      this.report.type = val;
      if (this.report.type == "student") this.showClasses = true;
      else this.showClasses = false;
    },

    openMessage() {
      this.msgObj = {
        dt: new Date().toJSON(),
        clsIDs: [0],
        secIDs: [0],
        status: "P",
      };
      this.visibility2 = true;

      var elem = this.$refs["msg_class"];
      elem.state = undefined;
      // var elem = this.$refs["msg_section"];
      // elem.state = undefined;
      var elem = this.$refs["msg_date"];
      elem.state = undefined;
    },

    async saveMessage() {
      // console.log(this.msgObj);
      if (
        this.msgClass.length == 0 ||
        this.msgSection.length == 0 ||
        this.msgObj.dt == ""
      ) {
        return this.$bvToast.toast(
          this.$t("Please select all the required fields"),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      } else {
        if (this.msgClass[0] !== 0) {
          this.msgObj.clsIDs = this.msgClass;
        } else {
          this.msgObj.clsIDs = [];
        }
        if (this.msgSection[0] !== 0) {
          //console.log(this.secID);
          this.msgObj.secIDs = this.msgSection;
        } else {
          this.msgObj.secIDs = [];
        }
        this.request = true;
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Attendance/SendMessage?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: this.msgObj,
          message: this.$t("Message send successfully!"),
          context: this,
          token: this.$store.state.userData.token,
        });
        this.request = false;
        if (status) {
          this.visibility2 = false;
        }
      }
    },
    FillMsgSections() {
      // console.log(this.msgClass);
      if (this.msgClass[0] == 0 && this.msgClass.length !== 1) {
        this.msgClass = this.msgClass.filter((e) => e !== 0);
        // console.log(this.msgClass);
        this.classes.forEach((elem) => {
          if (this.msgClass.includes(elem.cls.id)) {
            if (elem.sections.length != 0) {
              elem.sections.forEach((el) => this.msgSecOptions.push(el));
            } else {
              // console.log("null");
              this.msgSecOptions = [{ section: "All Sections", id: 0 }];
            }
          }
        });
      } else if (
        this.msgClass[this.msgClass.length - 1] !== 0 &&
        this.msgClass.length == 1
      ) {
        this.classes.forEach((elem) => {
          if (this.msgClass.includes(elem.cls.id)) {
            if (elem.sections.length != 0) {
              elem.sections.forEach((el) => this.msgSecOptions.push(el));
            } else {
              // console.log("null");
              this.msgSecOptions = [{ section: "All Sections", id: 0 }];
            }
          }
        });
      } else if (this.msgClass[this.msgClass.length - 1] == 0) {
        this.msgClass = [0];
        // console.log(this.msgClass);
        this.msgSecOptions = [{ section: "All Sections", id: 0 }];
        this.msgSection = [0];
      } else {
        this.msgSecOptions = [{ section: "All Sections", id: 0 }];
        this.msgSection = [0];
      }
      // this.CheckClass();
    },
    checkMsgSections() {
      // console.log(this.msgSection);
      if (this.msgSection[0] == 0 && this.msgSection.length !== 1) {
        this.msgSection = this.msgSection.filter((e) => e !== 0);
        // console.log(this.msgSection);
      } else if (this.msgSection[this.msgSection.length - 1] == 0) {
        this.msgSection = [0];
        // console.log(this.msgSection);
      }
    },

    FillSections() {
      //   if (this.classID.includes(0) && this.classID.length == 2) {
      //     this.classID = this.classID.filter((e) => e !== 0);
      //     console.log(this.classID);
      //   }

      // console.log(this.classID);
      if (this.classID[0] == 0 && this.classID.length !== 1) {
        this.classID = this.classID.filter((e) => e !== 0);
        // console.log(this.classID);
        this.classes.forEach((elem) => {
          if (this.classID.includes(elem.cls.id)) {
            if (elem.sections.length != 0) {
              elem.sections.forEach((el) => this.admittedSections.push(el));
            } else {
              // console.log("null");
              this.admittedSections = [
                { section: "All " + this.getLabel("Sections"), id: 0 },
              ];
            }
          }
        });
      } else if (
        this.classID[this.classID.length - 1] !== 0 &&
        this.classID.length == 1
      ) {
        this.classes.forEach((elem) => {
          if (this.classID.includes(elem.cls.id)) {
            if (elem.sections.length != 0) {
              elem.sections.forEach((el) => this.admittedSections.push(el));
            } else {
              // console.log("null");
              this.admittedSections = [
                { section: "All " + this.getLabel("Sections"), id: 0 },
              ];
            }
          }
        });
      } else if (this.classID[this.classID.length - 1] == 0) {
        this.classID = [0];
        // console.log(this.classID);
        this.admittedSections = [
          { section: "All " + this.getLabel("Sections"), id: 0 },
        ];
        this.secID = [0];
      } else {
        this.admittedSections = [
          { section: "All " + this.getLabel("Sections"), id: 0 },
        ];
        this.secID = [0];
      }
      // this.CheckClass();
    },
    checkSections() {
      // console.log(this.secID);
      if (this.secID[0] == 0 && this.secID.length !== 1) {
        this.secID = this.secID.filter((e) => e !== 0);
        // console.log(this.secID);
      } else if (this.secID[this.secID.length - 1] == 0) {
        this.secID = [0];
        // console.log(this.secID);
      }
      // this.CheckSec();
    },
    CheckSec() {
      var elem = this.$refs["section"];
      if (this.secID.length == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    FillRepSections() {
      // console.log(this.repClass);
      if (this.repClass[0] == 0 && this.repClass.length !== 1) {
        this.repClass = this.repClass.filter((e) => e !== 0);
        // console.log(this.repClass);
        this.classes.forEach((elem) => {
          if (this.repClass.includes(elem.cls.id)) {
            if (elem.sections.length != 0) {
              elem.sections.forEach((el) => this.repSecOptions.push(el));
            } else {
              // console.log("null");
              this.repSecOptions = [
                { section: "All " + this.getLabel("Sections"), id: 0 },
              ];
            }
          }
        });
      } else if (
        this.repClass[this.repClass.length - 1] !== 0 &&
        this.repClass.length == 1
      ) {
        this.classes.forEach((elem) => {
          if (this.repClass.includes(elem.cls.id)) {
            if (elem.sections.length != 0) {
              elem.sections.forEach((el) => this.repSecOptions.push(el));
            } else {
              // console.log("null");
              this.repSecOptions = [
                { section: "All " + this.getLabel("Sections"), id: 0 },
              ];
            }
          }
        });
      } else if (this.repClass[this.repClass.length - 1] == 0) {
        this.repClass = [0];
        // console.log(this.repClass);
        this.repSecOptions = [
          { section: "All " + this.getLabel("Sections"), id: 0 },
        ];
        this.repSection = [0];
      } else {
        this.repSecOptions = [
          { section: "All " + this.getLabel("Sections"), id: 0 },
        ];
        this.repSection = [0];
      }
      // this.CheckClass();
    },
    checkRepSections() {
      // console.log(this.repSection);
      if (this.repSection[0] == 0 && this.repSection.length !== 1) {
        this.repSection = this.repSection.filter((e) => e !== 0);
        // console.log(this.repSection);
      } else if (this.repSection[this.repSection.length - 1] == 0) {
        this.repSection = [0];
        // console.log(this.repSection);
      }
    },
    setRepDate(sd, dateStr) {
      // console.log(sd, dateStr);
      if (dateStr) {
        if (sd.length > 1) {
          let date = dateStr.split(" to ");
          if (date.length == 1) {
            this.repObj.dtFrom = date[0];
            this.repObj.dtTo = date[0];
          } else {
            this.repObj.dtFrom = date[0];
            this.repObj.dtTo = date[1];
          }
        } else {
          this.repObj.dtFrom = dateStr;
          this.repObj.dtTo = null;
        }
      } else {
        this.repObj.dtFrom = null;
        this.repObj.dtTo = null;
      }
      // console.log(this.repObj);
    },
    setStatus() {
      if (this.repObj.status[this.repObj.status.length - 1] == "") {
        this.repObj.status = [""];
      } else {
        this.repObj.status = this.repObj.status.filter((el) => el != "");
      }
      // console.log(this.repObj.status);
    },
    openReport() {
      const fd = new Date();
      const firstDate = `${fd.getFullYear()}-${(fd.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-01`;

      const ld = new Date(fd.getFullYear(), fd.getMonth() + 1, 0);
      const lastDate = `${ld.getFullYear()}-${(ld.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${ld.getDate().toString().padStart(2, "0")}`;

      // console.log(firstDate, lastDate);
      this.repObj = {
        type: "",
        dtFrom: firstDate,
        dtTo: lastDate,
        clsIDs: [0],
        secIDs: [0],
        status: [""],
      };
      this.repDate = `${firstDate} to ${lastDate}`;

      this.visibility3 = true;

      var elem = this.$refs["rep_class"];
      elem.state = undefined;
      // var elem = this.$refs["rep_section"];
      // elem.state = undefined;
      var elem = this.$refs["rep_date"];
      elem.state = undefined;
    },

    async getReport() {
      // console.log(this.repObj);
      if (
        this.repClass.length == 0 ||
        this.repSection.length == 0 ||
        this.repObj.status.length == 0 ||
        !this.repObj.dtFrom ||
        !this.repObj.dtTo
      ) {
        return this.$bvToast.toast(
          this.$t("Please select all the required fields"),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      } else {
        if (this.repClass[0] !== 0) {
          this.repObj.clsIDs = this.repClass;
        } else {
          this.repObj.clsIDs = "";
        }
        if (this.repSection[0] !== 0) {
          this.repObj.secIDs = this.repSection;
        } else {
          this.repObj.secIDs = "";
        }
        let status = "";
        if (this.repObj.status[0] !== "") {
          status = this.repObj.status;
        }
        this.request = true;

        var url =
          `https://${this.reportDomain}.myskool.app/Attendance/Show?type=` +
          (this.reportType == "" ? "student" : this.reportType) +
          "&dtFrom=" +
          this.repObj.dtFrom +
          "&dtTo=" +
          this.repObj.dtTo +
          "&dbb=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&cls=" +
          this.repObj.clsIDs +
          "&sec=" +
          this.repObj.secIDs +
          "&status=" +
          status;

        window.open(url, "_blank");
        this.request = false;
        // if (status) {
        //   this.visibility3 = false;
        // }
      }
    },
    async exportReport() {
      // console.log(this.repObj);
      if (
        this.repClass.length == 0 ||
        this.repSection.length == 0 ||
        this.repObj.status.length == 0 ||
        !this.repObj.dtFrom ||
        !this.repObj.dtTo
      ) {
        return this.$bvToast.toast(
          this.$t("Please select all the required fields"),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      } else {
        if (this.repClass[0] !== 0) {
          this.repObj.clsIDs = this.repClass;
        } else {
          this.repObj.clsIDs = "";
        }
        if (this.repSection[0] !== 0) {
          this.repObj.secIDs = this.repSection;
        } else {
          this.repObj.secIDs = "";
        }
        let status = "";
        if (this.repObj.status[0] !== "") {
          status = this.repObj.status;
        }
        this.exporting = true;

        var url =
          `https://${this.reportDomain}.myskool.app/Attendance/ShowExport?type=?type=` +
          (this.reportType == "" ? "student" : this.reportType) +
          "&dtFrom=" +
          this.repObj.dtFrom +
          "&dtTo=" +
          this.repObj.dtTo +
          "&dbb=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&cls=" +
          this.repObj.clsIDs +
          "&sec=" +
          this.repObj.secIDs +
          "&status=" +
          status;

        axios
          .get(url)
          .then((response) => {
            if (response.data.status == "success") {
              window.open(response.data.data, "_blank");
            } else {
              this.$bvToast.toast("Something went wrong", {
                title: this.$t("Error!"),
                variant: "danger",
                toaster: "b-toaster-top-center",
              });
            }
          })
          .catch((error) => {
            console.error("There was an error!", error);
          })
          .finally(() => {
            this.exporting = false;
          });
      }
    },
    async getSheet() {
      if (this.repClass.length == 0 || this.repSection.length == 0) {
        return this.$bvToast.toast("Please select class and section", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } else {
        if (this.repClass[0] !== 0) {
          this.repObj.clsIDs = this.repClass;
        } else {
          this.repObj.clsIDs = "";
        }
        if (this.repSection[0] !== 0) {
          this.repObj.secIDs = this.repSection;
        } else {
          this.repObj.secIDs = "";
        }

        this.sheetLoading = true;
        if (this.$store.state.currentBranch.organizationType == "coaching") {
          var url =
            `https://${this.reportDomain}.myskool.app/List/Show?type=attendance&dbb=` +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&cls=" +
            this.repObj.clsIDs +
            "&sec=" +
            this.repObj.secIDs +
            "&session=" +
            this.sessionID +
            "&status=&mob=false";

          window.open(url, "_blank");
        } else {
          var url =
            `https://${this.reportDomain}.myskool.app/List/Show?type=attendance&dbb=` +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&cls=" +
            this.repObj.clsIDs +
            "&sec=" +
            this.repObj.secIDs +
            "&status=&mob=false";

          window.open(url, "_blank");
        }
        this.sheetLoading = false;
      }
    },
    async loadgrid() {
      if (
        this.classID.length > 0 &&
        this.secID.length > 0 &&
        this.report.dtFrom
      ) {
        this.gridLoading = true;
        this.showGrid = true;

        if (this.classID[0] !== 0) {
          this.report.clsIDs = this.classID;
        } else {
          this.report.clsIDs = [];
        }
        if (this.secID[0] !== 0) {
          //console.log(this.secID);
          this.report.secIDs = this.secID;
        } else {
          this.report.secIDs = [];
        }
        // console.log(this.report);
        this.request = true;
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Attendance/LoadAttendanceGrid?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: this.report,
          attendance: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        this.request = false;
        this.loadSummary();
        if (status) {
          this.gridData = status;
          // this.gridData = this.gridData.map((el) => ({
          //   ...el,
          //   inputTimein: el.date,
          // }));
          this.sortData();
        }
        this.gridLoading = false;
      } else {
        this.$bvToast.toast(this.$t("Please select all required fields"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
    },
    async DeleteAtt() {
      if (
        this.classID.length > 0 &&
        this.secID.length > 0 &&
        this.report.dtFrom
      ) {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: this.$t("Do you want to delete the attendance?"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: this.$t("Yes, Delete it!"),
        });

        if (result.isConfirmed) {
          this.deleting = true;
          if (this.classID[0] !== 0) {
            this.report.clsIDs = this.classID;
          } else {
            this.report.clsIDs = [];
          }
          if (this.secID[0] !== 0) {
            //console.log(this.secID);
            this.report.secIDs = this.secID;
          } else {
            this.report.secIDs = [];
          }
          var status = await this.post({
            url:
              this.$store.state.domain +
              "Attendance/DeleteAttendace?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId,
            message: "Attendance deleted successfully!",
            context: this,
            body: this.report,
            token: this.$store.state.userData.token,
          });
          this.deleting = false;
          if (status) {
            this.loadgrid();
          }
        }
      } else {
        this.$bvToast.toast(this.$t("Please select all required fields"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
    },
    async GetStudent() {
      // remove leading 0s
      if (this.reportDomain == "myskoolmakjauhar") {
        this.studentID = this.studentID
          ? this.studentID.replace(/^0+/, "")
          : "";
      }
      // console.log(this.studentID);

      if (this.studentID.length !== 0) {
        this.studentLoading = true;
        var obj = {
          url:
            this.$store.state.domain +
            "Attendance/LoadStudentData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&gr=" +
            this.studentID +
            "&userID=" +
            this.$store.state.userData.userID,
          token: this.$store.state.userData.token,
        };
        this.currentStd = await this.get(obj);
        // console.log("res:", this.currentStd);
        this.studentLoading = false;

        if (this.currentStd == "No data found.") {
          this.currentStd = {
            std: {
              picture: null,
              cls: null,
              grNo: null,
              name: null,
            },
            dues: null,
          };
          return this.$bvToast.toast(this.$t("No student found."), {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
        } else if (this.currentStd == "Student's profile is not active.") {
          this.currentStd = {
            std: {
              picture: null,
              cls: null,
              grNo: null,
              name: null,
            },
            dues: null,
          };
          return this.$bvToast.toast("Student's profile is not active.", {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
        } else {
          // this.myObj = this.currentStd.std;
          // console.log("obj:", this.myObj);

          this.items.unshift(this.currentStd);
          // console.log(this.items);
        }
        this.studentID = "";
        if (this.settings.autoHide) {
          setTimeout(() => {
            this.currentStd = {
              std: {
                picture: null,
                cls: null,
                grNo: null,
                name: null,
              },
              dues: null,
            };
            this.studentID = "";
          }, this.settings.hideAfterSeconds * 1000);
        }
      }
    },
    CheckAccount() {
      var elem = this.$refs["account"];
      if (this.myObj.account == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckBalance() {
      var elem = this.$refs["balance"];
      var re = /^[0-9]+$/;
      if (re.test(this.myObj.balance) && this.myObj.balance !== 0) {
        this.myObj.balance = parseInt(this.myObj.balance);
        return (elem.state = true);
      } else {
        return (elem.state = false);
      }
    },

    hideSideBar() {
      this.visibility = false;
    },

    hideAtt() {
      this.editAtt = false;
    },

    showSmart() {
      const dt = new Date().toJSON().split("T")[0];
      this.smartObj.dt = `${dt} to ${dt}`;
      this.smartObj.dtFrom = dt;
      this.smartObj.dtTo = dt;
      this.visibility4 = true;
    },
    setSmartDate(sd, dateStr) {
      // console.log(sd, dateStr);
      if (dateStr) {
        if (sd.length > 1) {
          let date = dateStr.split(" to ");
          if (date.length == 1) {
            this.smartObj.dtFrom = date[0];
            this.smartObj.dtTo = date[0];
          } else {
            this.smartObj.dtFrom = date[0];
            this.smartObj.dtTo = date[1];
          }
        } else {
          this.smartObj.dtFrom = dateStr;
          this.smartObj.dtTo = null;
        }
      } else {
        this.smartObj.dtFrom = null;
        this.smartObj.dtTo = null;
      }
      // console.log(this.smartObj);
    },
    async saveSmart() {
      const grFormatted = this.smartObj.gr.trim().replace(/\n|,/g, " ");
      if (
        grFormatted.trim() == "" ||
        !this.smartObj.dtFrom ||
        !this.smartObj.dtTo ||
        this.smartObj.status == ""
      ) {
        this.$bvToast.toast(this.$t("Please enter the details properly"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } else {
        this.smartObj.gr = grFormatted;
        // console.log(this.smartObj);
        this.request = true;
        // SmartAttendance
        const payload = {
          status: this.smartObj.status,
          gr: grFormatted,
          dt: this.smartObj.dtFrom,
          dtTo: this.smartObj.dtTo,
        };
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Attendance/SmartAttendanceRange?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: payload,
          message: "Attendance saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.request = false;
        if (status) {
          this.visibility4 = false;
        }
      }
    },

    showSettings() {
      this.visibility = true;
    },
    async LoadSettings() {
      this.dataLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "AttendanceSettings/LoadData?campusID=" +
          this.$store.state.userData.cId +
          "&db=" +
          this.$store.state.userData.db,
        token: this.$store.state.userData.token,
      };
      let result = await this.get(obj);
      this.dataLoading = false;
      if (result == null) {
        this.settings = {
          id: 0,
          campusID: this.$store.state.userData.cId,
          notifyArrival: false,
          notifyDeparture: false,
          showDues: false,
          showRecent: false,
          autoHide: false,
          hideAfterSeconds: 5,
          attendanceMode: "",
        };
      } else {
        this.settings = result;
      }
      // console.log("set", this.settings);
    },
    async LoadClasses() {
      var obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      this.classes = await this.get(obj);
      // console.log("class", this.classes);
      this.classes.forEach((el) => {
        this.classesOptions.push(el.cls);
      });
    },
    async LoadDepartments() {
      var obj = {
        url:
          this.$store.state.domain +
          "departments?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.departments = await this.get(obj);
      if (this.departments.length > 0) {
        this.departments.unshift({ name: "All Departments", id: 0 });
      }
    },

    async saveSettings() {
      this.request = true;
      let x = parseInt(this.settings.hideAfterSeconds);
      this.settings.hideAfterSeconds = !x ? 5 : x;

      var status = await this.post({
        url:
          this.$store.state.domain +
          "AttendanceSettings/Save?db=" +
          this.$store.state.userData.db,
        body: this.settings,
        message: "Settings saved successfully.",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.request = false;
      if (status) {
        this.visibility = false;
        this.LoadSettings();
      }
    },
    async checkPrint(text) {
      if (this.gridData.length === 0) {
        this.$bvToast.toast(
          this.$t("No attendance data to send notification."),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
        return;
      }

      let result = await Swal.fire({
        html: `<div class='text-left'><p class='mb-50'>${this.$t(
          "You are about to send attendance notification and apply attendance."
        )}</p>
         <p class='mt-1'>${this.$t("Are you sure you want to proceed?")}</p>
         </div>`,
        icon: "warning",
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes"),
        cancelButtonText: this.$t("Cancel"),
      });

      if (result.isConfirmed) {
        if (text == "notification") {
          await this.sendNotification();
        }
      }
    },
    async sendNotification() {
      this.iNotification = true;

      try {
        // First, save the attendance
        if (this.gridData.length != 0) {
          var status = await this.post({
            url:
              this.$store.state.domain +
              "Attendance/SaveWeb?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId,
            body: this.gridData,
            context: this,
            token: this.$store.state.userData.token,
          });

          if (status) {
            this.loadgrid();
          }
        }

        // Prepare class and section IDs for notification
        let clsIDs = [];
        let secIDs = [];

        if (this.classID[0] !== 0) {
          clsIDs = this.classID;
        } else {
          clsIDs = [];
        }

        if (this.secID[0] !== 0) {
          secIDs = this.secID;
        } else {
          secIDs = [];
        }

        // Get current date for notification (format: Y-m-d, e.g., "2025-11-13")
        let currentDate = this.report.dtFrom;
        if (!currentDate) {
          const today = new Date();
          currentDate = `${today.getFullYear()}-${String(
            today.getMonth() + 1
          ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        }
        // Ensure date format is Y-m-d
        if (currentDate.includes("T")) {
          currentDate = currentDate.split("T")[0];
        }

        // Prepare notification payload
        const notificationObj = {
          dt: currentDate, // Date in format "2025-11-13"
          clsIDs: clsIDs, // Array of class IDs, e.g., [1, 2, 3]
          secIDs: secIDs, // Array of section IDs, e.g., [1, 5] or []
        };

        // Send notification using Attendance/SendNotification endpoint
        var notificationStatus = await this.post({
          url:
            this.$store.state.domain +
            "Attendance/SendNotification?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: notificationObj,
          context: this,
          token: this.$store.state.userData.token,
        });

        if (notificationStatus) {
          this.$bvToast.toast("Notification sent successfully!", {
            title: "Success!",
            variant: "success",
            toaster: "b-toaster-top-center",
          });
        }
      } catch (error) {
        console.error("Error sending notification:", error);
        this.$bvToast.toast("Failed to send notification. Please try again.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.iNotification = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/apps/calendar.scss";
</style>
<style scoped>
.image {
  float: left;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  border: 1px solid #ebebeb;
  margin: 5px;
}

.card {
  transition: 0.4s;
}

.card:hover {
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
}

/* .card-body {
  padding: 0.85rem;
} */
@keyframes myfade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.set-picSize {
  width: 180px !important;
  height: 180px !important;
}
</style>

<!-- Days Settings sidebar only (unscoped so sidebar-class width applies) -->
<style lang="scss">
.att-calendar-event-cell .fc-event-main {
  color: inherit !important;
}

#sidebar-days-settings.sidebar-days-settings,
.b-sidebar.sidebar-days-settings {
  width: 42rem !important;
  max-width: 96vw !important;
}

#sidebar-days-settings .days-settings,
.sidebar-days-settings .days-settings {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f6fa;
}

#sidebar-days-settings .days-settings__header,
.sidebar-days-settings .days-settings__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.1rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

#sidebar-days-settings .days-settings__body,
.sidebar-days-settings .days-settings__body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem 1.5rem;
}

#sidebar-days-settings .days-settings__card,
.sidebar-days-settings .days-settings__card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem 1.1rem;
  margin-bottom: 1rem;
}

#sidebar-days-settings .days-settings__card-title,
.sidebar-days-settings .days-settings__card-title {
  font-size: 0.98rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.7rem;
}

#sidebar-days-settings .days-settings__list-head,
.sidebar-days-settings .days-settings__list-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.25rem 0 0.85rem;
}

#sidebar-days-settings .days-settings__empty,
.sidebar-days-settings .days-settings__empty {
  text-align: center;
  background: #fff;
  border: 1px dashed #d1d5db;
  border-radius: 14px;
  padding: 1.75rem 1rem;
  color: #6b7280;
}

#sidebar-days-settings .days-settings__rule,
.sidebar-days-settings .days-settings__rule {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  padding: 0.85rem 1rem;
}

#sidebar-days-settings .days-settings__rule-top,
.sidebar-days-settings .days-settings__rule-top {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}

#sidebar-days-settings .days-settings__meta-grid,
.sidebar-days-settings .days-settings__meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
  flex: 1;
  min-width: 0;
}

#sidebar-days-settings .days-settings__meta-card,
.sidebar-days-settings .days-settings__meta-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: 0.55rem 0.75rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

#sidebar-days-settings .days-settings__meta-label,
.sidebar-days-settings .days-settings__meta-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  line-height: 1.2;
  margin-bottom: 0.2rem;
}

#sidebar-days-settings .days-settings__meta-value,
.sidebar-days-settings .days-settings__meta-value {
  display: block;
  font-size: 0.92rem;
  font-weight: 600;
  color: #212529;
  line-height: 1.3;
  word-break: break-word;
}

#sidebar-days-settings .days-settings__days-wrap,
.sidebar-days-settings .days-settings__days-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}

#sidebar-days-settings .days-settings__day,
.sidebar-days-settings .days-settings__day {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.2rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background: #e9ecef;
  color: #495057;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

#sidebar-days-settings .days-settings__day--empty,
.sidebar-days-settings .days-settings__day--empty {
  background: #fff3cd;
  color: #856404;
}

#sidebar-days-settings .days-settings__actions--icons,
.sidebar-days-settings .days-settings__actions--icons {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  border: none;
  align-self: center;
}

#sidebar-days-settings .days-settings__icon-btn,
.sidebar-days-settings .days-settings__icon-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

#sidebar-days-settings .days-settings__pager,
.sidebar-days-settings .days-settings__pager {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.35rem;
}

#sidebar-days-settings .days-settings-skeleton,
.sidebar-days-settings .days-settings-skeleton {
  width: 100%;
}

#sidebar-days-settings .days-settings-skeleton__card,
.sidebar-days-settings .days-settings-skeleton__card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.75rem;
}

#sidebar-days-settings .days-settings-skeleton__top,
.sidebar-days-settings .days-settings-skeleton__top {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}

#sidebar-days-settings .days-settings-skeleton__meta-grid,
.sidebar-days-settings .days-settings-skeleton__meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
  flex: 1;
  min-width: 0;
}

#sidebar-days-settings .days-settings-skeleton__actions,
.sidebar-days-settings .days-settings-skeleton__actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  align-self: center;
}

#sidebar-days-settings .days-settings-skeleton__chips,
.sidebar-days-settings .days-settings-skeleton__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

#sidebar-days-settings .days-settings-skeleton__bar,
.sidebar-days-settings .days-settings-skeleton__bar {
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #e2e5ea 0%,
    #f3f4f6 35%,
    #e2e5ea 70%
  );
  background-size: 200% 100%;
  animation: daysSettingsSkeletonShine 1.15s ease-in-out infinite;
}

#sidebar-days-settings .days-settings-skeleton__bar--meta,
.sidebar-days-settings .days-settings-skeleton__bar--meta {
  height: 52px;
  width: 100%;
  border-radius: 8px;
}

#sidebar-days-settings .days-settings-skeleton__bar--icon,
.sidebar-days-settings .days-settings-skeleton__bar--icon {
  height: 32px;
  width: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}

#sidebar-days-settings .days-settings-skeleton__bar--chip,
.sidebar-days-settings .days-settings-skeleton__bar--chip {
  height: 24px;
  width: 2.5rem;
  border-radius: 4px;
}

@keyframes daysSettingsSkeletonShine {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

/* ── Student Holiday History Modal ─────────────────── */
.holiday-history-modal {
  border-radius: 14px;
  overflow: hidden;
}
.hh-modal {
  background: #fff;
  font-family: inherit;
}
.hh-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #efeff7;
  background: #fafaff;
}
.hh-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.hh-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ece8ff;
  color: #7367f0;
}
.hh-header-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2f2b3d;
  line-height: 1.2;
}
.hh-header-sub {
  font-size: 0.78rem;
  color: #8c879c;
  margin-top: 2px;
}
.hh-close-btn {
  border: 0;
  background: #f1f0f7;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6e6b7b;
  cursor: pointer;
}
.hh-close-btn:hover {
  background: #e8e6f3;
  color: #2f2b3d;
}
.hh-filters {
  display: grid;
  grid-template-columns: 1fr 1fr minmax(160px, 1fr) auto;
  gap: 12px;
  align-items: end;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f7;
}
.hh-filter-field label {
  display: block;
  margin-bottom: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6e6b7b;
}
.hh-date-input {
  min-height: 38px;
  border-radius: 8px;
}
.hh-filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.hh-btn-search,
.hh-btn-clear {
  min-height: 38px;
  border-radius: 8px !important;
}
.hh-btn-search {
  display: inline-flex;
  align-items: center;
  padding-inline: 14px !important;
}
.hh-table-wrap {
  padding: 0 8px 8px;
  max-height: 52vh;
  overflow: auto;
}
.holiday-history-table {
  margin-bottom: 0;
}
.holiday-history-table thead th.hh-th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  border-top: 0;
  border-bottom: 1px solid #efeff7;
  color: #8c879c;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  padding: 12px 14px;
}
.holiday-history-table tbody td.hh-td {
  padding: 12px 14px;
  vertical-align: middle;
  border-top: 1px solid #f5f4fa;
  color: #4b465c;
  font-size: 0.875rem;
}
.hh-th-actions,
.hh-td-actions {
  width: 56px;
  text-align: center !important;
}
.hh-campus {
  font-weight: 600;
  color: #2f2b3d;
}
.hh-date-chip {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 6px;
  background: #f3f2f7;
  color: #5e5873;
  font-size: 0.8rem;
  white-space: nowrap;
}
.hh-reason {
  display: inline-block;
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hh-status-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}
.hh-status-badge {
  font-size: 0.72rem;
  font-weight: 600;
}
.hh-status-meta {
  font-size: 0.68rem;
  color: #8c879c;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hh-dept-pill {
  display: inline-flex;
  padding: 3px 9px;
  border-radius: 999px;
  background: #eefbf3;
  color: #28c76f;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
.hh-muted {
  color: #8c879c;
  white-space: nowrap;
}
.hh-actions {
  display: flex;
  justify-content: center;
}
.hh-del-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: #fdeeee;
  color: #ea5455;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.hh-del-btn:hover:not(:disabled) {
  background: #ea5455;
  color: #fff;
}
.hh-del-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.hh-busy,
.hh-empty {
  text-align: center;
  padding: 2rem 1rem;
  color: #8c879c;
}
.hh-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px 16px;
  border-top: 1px solid #efeff7;
  background: #fafaff;
}
.hh-footer-count {
  font-size: 0.8rem;
  color: #8c879c;
}
@media (max-width: 768px) {
  .hh-filters {
    grid-template-columns: 1fr;
  }
}

/* ── Holiday Summary Modal ─────────────────────────── */
.hs-modal {
  border-radius: 12px;
  overflow: hidden;
  font-family: inherit;
  background: #fff;
}
.hs-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #f0f0f7;
  background: #fafafe;
}
.hs-header-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.hs-header-icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  background: #e6f9ee;
  color: #28a745;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hs-header-icon--warn {
  background: #fff4e5;
  color: #ff9f43;
}
.hs-header-title {
  font-size: 0.93rem;
  font-weight: 700;
  color: #2d2d3a;
  line-height: 1.3;
  margin-bottom: 6px;
}
.hs-header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.hs-meta-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.71rem;
  font-weight: 500;
  color: #6e6b7b;
  background: #f0eef8;
  border-radius: 20px;
  padding: 2px 9px;
}
.hs-meta-badge--reason {
  background: #fff3e0;
  color: #d87c2a;
}
.hs-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #bbb;
  padding: 4px;
  border-radius: 6px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
}
.hs-close-btn:hover {
  background: #eeeef5;
  color: #555;
}
.hs-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-bottom: 1px solid #f0f0f7;
}
.hs-stat {
  padding: 16px 8px;
  text-align: center;
  border-right: 1px solid #f0f0f7;
}
.hs-stat:last-child {
  border-right: none;
}
.hs-stat-num {
  font-size: 1.65rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 4px;
}
.hs-stat-lbl {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #b0aec0;
}
.hs-stat--green .hs-stat-num {
  color: #28c76f;
}
.hs-stat--blue .hs-stat-num {
  color: #00cfe8;
}
.hs-stat--orange .hs-stat-num {
  color: #ff9f43;
}
.hs-stat--purple .hs-stat-num {
  color: #7367f0;
}
.hs-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 22px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f3f2fd;
  color: #5e5873;
  font-size: 0.8rem;
  line-height: 1.4;
}
.hs-message--warn {
  background: #fff8ef;
  color: #9a6700;
  border: 1px solid #ffe2b8;
}
.hs-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 22px 14px;
  border-top: 1px solid #f0f0f7;
  background: #fafafe;
}
.hs-done-btn {
  padding: 6px 22px !important;
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}
</style>
